import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import Loader from 'react-loader-spinner';
import { FiArrowUp, FiChevronDown, FiChevronUp, FiDownload, FiExternalLink, FiImage, FiMaximize2, FiUpload, FiX } from 'react-icons/fi';
import ImageCropper from '../components/ImageCropper';
import AdminPlayerPicker from '../components/AdminPlayerPicker';
import SponsorOverlay from '../components/SponsorOverlay';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { useAuth } from '../utilities/auth';
import { ADMIN_READ_ROLES, hasAnyRole, ROLE } from '../utilities/adminAccess';
import { useChampionships } from '../utilities/championships';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { champFields, deriveGroupsFromServer, extractEntity, extractList as extractTournamentList, normalizeSeasonTeam } from '../utilities/tournamentsApi';
import { extractMatchesPage, formatKickoff, matchRequestBody } from '../utilities/matchesApi';
import {
  DEFAULT_FOOTER,
  DEFAULT_FOOTER_COLOR,
  FALLBACK_V2_TYPES,
  PATTERN_UNDERLOZHKA_KEYS,
  PICTURE_TYPE_TABLE,
  PICTURE_TYPE_TOP10,
  PICTURE_TYPE_RESULTS,
  PICTURE_TYPE_CHESS,
  PICTURE_TYPE_GROUP_TABLES,
  PICTURE_TYPE_CALENDAR,
  PICTURE_TYPE_CARDS,
  PICTURE_TYPE_DISQUALIFICATIONS,
  PICTURE_TYPE_AFISHA,
  PICTURE_TYPE_PLAYOFF,
  PICTURE_TYPE_PREVIEW,
  PICTURE_TYPE_DETAILED_RESULT,
  PICTURE_TYPE_ANNOUNCEMENT,
  PICTURE_TYPE_QUOTE,
  PICTURE_TYPE_ACHIEVEMENTS,
  PICTURE_TYPE_MATCH_COVER_GOAL,
  PICTURE_TYPE_BROADCAST_PREVIEW,
  PICTURE_TYPE_TOUR_TEAM,
  PICTURE_TYPE_MATCH_STATS,
  PICTURE_TYPE_MVP,
  PICTURE_TYPE_MATCH_SUBS,
  PICTURE_TYPE_MATCH_LINEUPS,
  PREVIEW_CANVAS_SIZE,
  QUOTE_CANVAS_SIZE,
  ACHIEVEMENTS_CANVAS_SIZE,
  MVP_CANVAS_SIZE,
  MATCH_COVER_CANVAS_HEIGHT,
  TOUR_TEAM_CANVAS_HEIGHT,
  PREVIEW_PHOTO_ASPECT,
  PREVIEW_PHOTO_SLOT,
  isMatchListPictureType,
  isSingleMatchPictureType,
  isMatchCoverGoalPictureType,
  isBroadcastPreviewPictureType,
  isTourTeamPictureType,
  isPlayoffStageSelectionValid,
  MATCH_LINEUP_SLOT_OPTIONS,
  MATCH_SUBS_POSITION_OPTIONS,
  buildMatchSubsPositionOverrides,
  isMatchRosterPictureType,
  mapMatchSubsRosterFromApi,
  mergePictureTypesCatalog,
  mergePlayoffAvailableStages,
  mergePlayoffAvailableTracks,
  normalizePlayoffAvailableTeam,
  playoffSelectionHasFinal,
  resolveTourTeamId,
  UNDERLOZHKA_CANVAS_WIDTH,
  compressDataUrl,
  createFallbackCatalog,
  defaultTitleForPictureType,
  deleteTournamentTableScheme,
  deleteTournamentTableSponsorScheme,
  emptySponsorsCache,
  emptyStylesCache,
  fetchMatchListCandidates,
  fetchSocialPictureHtml,
  fetchSocialPicturePngBlob,
  fetchTournamentTableSponsorsCache,
  fetchTournamentTableStyles,
  fetchUnderlozhkiCatalog,
  fetchV2PictureTypes,
  formatMatchListCandidateLabel,
  getCanvasWidthForPictureType,
  getSocialPictureErrorMessage,
  getUnderlozhkaAspect,
  readImageFileAsDataUrl,
  saveTournamentTableLeagueStyle,
  saveTournamentTableScheme,
  saveTournamentTableSponsorScheme,
  stylePreviewLabel,
  styleToFormFields,
} from '../utilities/socialPicturesApi';
import {
  buildSponsorsApiPayload,
  createSponsorPlacement,
  hydrateSponsorPlacement,
  isSponsorInLowerHalf,
  normalizePoolItem,
  resolveSponsorLayout,
  syncSponsorBottomOffset,
  triggerBlobDownload,
} from '../utilities/sponsorPool';
import { resetMobileViewport } from '../utilities/mobileViewport';
import '../styles/infographics.css';
import '../styles/sponsorOverlay.css';
import '../styles/createMatchModal.css';

/** Полевые staff-роли: для них «Мои назначения» включаем по умолчанию. */
const INFOGRAPHICS_STAFF_MATCH_ROLES = [
  ROLE.operator,
  ROLE.journalist,
  ROLE.photographer,
  ROLE.doctor,
  ROLE.referee,
];

const resolvePlayerTeamId = (player) => {
  if (!player || typeof player !== 'object') return null;
  const tid = Number(
    player.team_id
    || player.teamId
    || player.team?.id
    || player.team?.team_id
  );
  return Number.isFinite(tid) && tid > 0 ? tid : null;
};

const createMatchSubsPlayerRow = () => ({
  key: `ms-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  player_id: undefined,
  player_number: '',
  family: '',
  name: '',
  position: '',
  lineup_position_id: '',
});

const createEmptyMatchSubsRoster = () => ({
  starters: { home: [createMatchSubsPlayerRow()], guest: [createMatchSubsPlayerRow()] },
  bench: { home: [createMatchSubsPlayerRow()], guest: [createMatchSubsPlayerRow()] },
});

const ensureMatchSubsSideRows = (list) => (
  Array.isArray(list) && list.length ? list : [createMatchSubsPlayerRow()]
);

const flattenMatchSubsRosterRows = (roster) => ([
  ...(roster?.starters?.home || []),
  ...(roster?.starters?.guest || []),
  ...(roster?.bench?.home || []),
  ...(roster?.bench?.guest || []),
]);

/**
 * @param {object} apiRoster
 * @param {{ positionsAsOverrides?: boolean }} [options]
 *   positionsAsOverrides — keep API ampлуа in db_position, leave position empty
 *   so positionOverrides are sent only after the user types.
 */
const seedMatchSubsRoster = (apiRoster, { positionsAsOverrides = false } = {}) => {
  const mapped = mapMatchSubsRosterFromApi(apiRoster);
  const mapSide = (rows) => ensureMatchSubsSideRows(
    (rows || []).map((row) => {
      if (!positionsAsOverrides) return row;
      const fromApi = String(row.position || row.db_position || '').trim();
      return {
        ...row,
        db_position: fromApi,
        position: '',
      };
    })
  );
  return {
    starters: {
      home: mapSide(mapped.starters.home),
      guest: mapSide(mapped.starters.guest),
    },
    bench: {
      home: mapSide(mapped.bench.home),
      guest: mapSide(mapped.bench.guest),
    },
  };
};

/** Keep user position overrides when re-seeding from DB. */
const mergeMatchSubsRosterPositions = (seeded, previous) => {
  const positionByPlayerId = {};
  flattenMatchSubsRosterRows(previous).forEach((row) => {
    const playerId = Number(row?.player_id);
    const position = String(row?.position || '').trim();
    if (Number.isFinite(playerId) && playerId > 0 && position) {
      positionByPlayerId[playerId] = position;
    }
  });
  const applySide = (rows) => (rows || []).map((row) => {
    const playerId = Number(row?.player_id);
    const kept = Number.isFinite(playerId) && playerId > 0
      ? positionByPlayerId[playerId]
      : '';
    return kept ? { ...row, position: kept } : row;
  });
  return {
    starters: {
      home: applySide(seeded.starters.home),
      guest: applySide(seeded.starters.guest),
    },
    bench: {
      home: applySide(seeded.bench.home),
      guest: applySide(seeded.bench.guest),
    },
  };
};

const playerFieldsFromSearchPick = (player) => {
  if (!player || typeof player !== 'object') return null;
  const playerId = Number(player.id || player.player_id);
  const number = String(
    player.number
    || player.player_number
    || player.match_number
    || player.num
    || ''
  ).trim();
  let family = String(player.family || '').trim();
  let name = String(player.name || '').trim();
  if (!family && !name && player.player_name) {
    const parts = String(player.player_name).trim().split(/\s+/);
    family = parts[0] || '';
    name = parts.slice(1).join(' ');
  }
  return {
    player_id: Number.isFinite(playerId) && playerId > 0 ? playerId : undefined,
    player_number: number,
    family,
    name,
    position: String(player.position_short || player.position || '').trim(),
    lineup_position_id: String(
      player.lineup_position_id || player.lineupPositionId || player.slot || ''
    ).trim(),
  };
};

const DEFAULT_CANVAS_WIDTH = UNDERLOZHKA_CANVAS_WIDTH;
const PREVIEW_DEBOUNCE_MS = 400;
const DEFAULT_THEME = 'afl_long';

const underGroupFromTheme = (themeKey) => {
  const key = String(themeKey || '').trim().toLowerCase();
  if (!key) return 'pattern';
  if (key.endsWith('_long') || PATTERN_UNDERLOZHKA_KEYS.includes(key)) return 'pattern';
  return 'solid';
};

const resetMobileViewportAfterSelect = () => {
  resetMobileViewport();
};

const toColorInputValue = (value, fallback = '#000000') => {
  const parsed = parseCssColor(value) || parseCssColor(fallback);
  if (!parsed) return '#000000';
  return rgbToHex(parsed.r, parsed.g, parsed.b);
};

const clamp01 = (value, fallback = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(1, Math.max(0, num));
};

const parseCssColor = (value) => {
  const text = String(value || '').trim();
  if (!text) return null;

  if (/^#[0-9a-fA-F]{6}$/i.test(text)) {
    return {
      r: parseInt(text.slice(1, 3), 16),
      g: parseInt(text.slice(3, 5), 16),
      b: parseInt(text.slice(5, 7), 16),
      a: 1,
    };
  }

  if (/^#[0-9a-fA-F]{3}$/i.test(text)) {
    return {
      r: parseInt(text[1] + text[1], 16),
      g: parseInt(text[2] + text[2], 16),
      b: parseInt(text[3] + text[3], 16),
      a: 1,
    };
  }

  const rgba = text.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)$/i);
  if (rgba) {
    return {
      r: Number(rgba[1]),
      g: Number(rgba[2]),
      b: Number(rgba[3]),
      a: rgba[4] == null ? 1 : clamp01(rgba[4], 1),
    };
  }

  return null;
};

const rgbToHex = (r, g, b) => (
  `#${[r, g, b].map((part) => Math.round(Number(part) || 0).toString(16).padStart(2, '0')).join('')}`
);

const formatRgba = (r, g, b, a) => (
  `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${Number(a).toFixed(2).replace(/\.?0+$/, '') || '0'})`
);

function ColorField({
  id,
  label,
  value,
  placeholder,
  onChange,
  onReset,
  resetLabel,
  withAlpha = false,
  alphaLabel,
}) {
  const source = parseCssColor(value) || parseCssColor(placeholder) || { r: 0, g: 0, b: 0, a: withAlpha ? 0.48 : 1 };
  const alpha = clamp01(source.a, withAlpha ? 0.48 : 1);
  const hex = rgbToHex(source.r, source.g, source.b);

  const emitRgba = (r, g, b, nextAlpha) => {
    onChange(formatRgba(r, g, b, clamp01(nextAlpha, alpha)));
  };

  return (
    <div className={`ig-color-field${withAlpha ? ' has-alpha' : ''}`}>
      <div className="ig-field-label-row">
        <label className="ig-field-label" htmlFor={id}>{label}</label>
        {value ? (
          <button type="button" className="ig-clear-btn" onClick={onReset}>{resetLabel}</button>
        ) : null}
      </div>
      <div className="ig-color-control">
        <input
          id={id}
          type="color"
          value={hex}
          onChange={(event) => {
            const next = parseCssColor(event.target.value);
            if (!next) return;
            if (withAlpha) {
              emitRgba(next.r, next.g, next.b, alpha);
              return;
            }
            onChange(event.target.value);
          }}
        />
        <input
          className="ig-input"
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
        />
      </div>
      {withAlpha ? (
        <div className="ig-alpha-control">
          <label className="ig-alpha-label" htmlFor={`${id}-alpha`}>
            <span>{alphaLabel}</span>
            <span className="ig-alpha-value">{Math.round(alpha * 100)}%</span>
          </label>
          <input
            id={`${id}-alpha`}
            type="range"
            min="0"
            max="100"
            step="1"
            value={Math.round(alpha * 100)}
            onChange={(event) => {
              emitRgba(source.r, source.g, source.b, Number(event.target.value) / 100);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

function UnderlozhkaThumb({ src, accent, backgroundColor, label }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        className="ig-under-thumb"
        src={src}
        alt={label || ''}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span
      className="ig-under-fallback"
      style={{ background: `linear-gradient(160deg, ${accent || '#1550F5'}, ${backgroundColor || '#2A3A8F'})` }}
    />
  );
}

function UnderlozhkaGrid({ items, activeKey, isCustomBg, onSelect, uploadSlot }) {
  return (
    <div className="ig-under-grid">
      {items.map((item) => {
        const active = !isCustomBg && activeKey === item.key;
        const label = item.label || item.key;
        return (
          <button
            key={item.key}
            type="button"
            className={`ig-under-btn${active ? ' is-active' : ''}`}
            onClick={() => onSelect(item.key)}
            title={label}
            style={{ '--ig-theme-accent': item.accent }}
          >
            <UnderlozhkaThumb
              src={item.preview}
              accent={item.accent}
              backgroundColor={item.backgroundColor}
              label={label}
            />
            <span className="ig-under-label">{label}</span>
          </button>
        );
      })}
      {uploadSlot || null}
    </div>
  );
}

function Infographics() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t, lang } = useI18n();
  const { userData } = useAuth();
  const { champId, loading: champsLoading } = useChampionships();
  const fileInputRef = useRef(null);
  const previewLeftFileRef = useRef(null);
  const previewRightFileRef = useRef(null);
  // Toggle виден всем, кто открыл инфографику (5–13). Иначе админ с «добавочным»
  // оператором без перелогина / только с ролью 5–7 не видит фильтр вообще.
  const canUseMyStaffMatches = useMemo(
    () => hasAnyRole(userData, ADMIN_READ_ROLES) || Boolean(userData),
    [userData]
  );
  const preferMyStaffMatchesDefault = useMemo(
    () => hasAnyRole(userData, INFOGRAPHICS_STAFF_MATCH_ROLES)
      && !hasAnyRole(userData, [ROLE.root, ROLE.head, ROLE.administrator]),
    [userData]
  );

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);

  const [catalog, setCatalog] = useState(() => createFallbackCatalog());
  const [pictureTypes, setPictureTypes] = useState(() => mergePictureTypesCatalog(FALLBACK_V2_TYPES));
  const [pictureType, setPictureType] = useState(PICTURE_TYPE_TABLE);
  const [scheduleFilterMode, setScheduleFilterMode] = useState('stage');
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [scheduleDateFrom, setScheduleDateFrom] = useState('');
  const [scheduleDateTo, setScheduleDateTo] = useState('');
  const [includeUpcoming, setIncludeUpcoming] = useState(true);
  const [matchListOnlyMine, setMatchListOnlyMine] = useState(false);
  const [matchListCandidates, setMatchListCandidates] = useState([]);
  const [matchListDays, setMatchListDays] = useState([]);
  const [matchListSelectedIds, setMatchListSelectedIds] = useState([]);
  const [matchListLoading, setMatchListLoading] = useState(false);
  const [playoffVerticalLayout, setPlayoffVerticalLayout] = useState(false);
  const [playoffAvailableTracks, setPlayoffAvailableTracks] = useState([]);
  const [playoffAvailableStages, setPlayoffAvailableStages] = useState([]);
  const [playoffAvailableTeams, setPlayoffAvailableTeams] = useState([]);
  const [playoffSelectedTrackKeys, setPlayoffSelectedTrackKeys] = useState([]);
  const [playoffSelectedStageIds, setPlayoffSelectedStageIds] = useState([]);
  const [playoffSelectedTeamIds, setPlayoffSelectedTeamIds] = useState([]);
  const [playoffAllowMirror, setPlayoffAllowMirror] = useState(true);
  const [seasonGroups, setSeasonGroups] = useState([]);
  const [selectedGroupNames, setSelectedGroupNames] = useState([]);
  const [splitByGroups, setSplitByGroups] = useState(true);
  const [calendarColumns, setCalendarColumns] = useState(3);
  const [groupStagesOnly, setGroupStagesOnly] = useState(false);
  const [afishaDate, setAfishaDate] = useState('');
  const [afishaCode, setAfishaCode] = useState('');
  const [previewMatches, setPreviewMatches] = useState([]);
  const [selectedPreviewMatch, setSelectedPreviewMatch] = useState(null);
  const [previewLeftPhoto, setPreviewLeftPhoto] = useState('');
  const [previewRightPhoto, setPreviewRightPhoto] = useState('');
  const [previewLeftFileName, setPreviewLeftFileName] = useState('');
  const [previewRightFileName, setPreviewRightFileName] = useState('');
  const [quoteTeams, setQuoteTeams] = useState([]);
  const [selectedQuoteTeam, setSelectedQuoteTeam] = useState(null);
  const [quotePersonName, setQuotePersonName] = useState('');
  const [quoteText, setQuoteText] = useState('');
  const [achievementName, setAchievementName] = useState('');
  const [tourTeams, setTourTeams] = useState([]);
  const [selectedTourTeam, setSelectedTourTeam] = useState(null);
  const [matchStatsHomeBarColor, setMatchStatsHomeBarColor] = useState('');
  const [matchStatsGuestBarColor, setMatchStatsGuestBarColor] = useState('');
  const [matchStatsBarMeta, setMatchStatsBarMeta] = useState(null);
  const [mvpPlayer, setMvpPlayer] = useState(null);
  const [selectedMvpTeam, setSelectedMvpTeam] = useState(null);
  const [matchSubsManual, setMatchSubsManual] = useState(false);
  const [matchSubsEditPositions, setMatchSubsEditPositions] = useState(false);
  const [matchSubsRoster, setMatchSubsRoster] = useState(() => createEmptyMatchSubsRoster());
  const [matchSubsDbRoster, setMatchSubsDbRoster] = useState(null);
  const [matchSubsTeamNames, setMatchSubsTeamNames] = useState({ home: '', guest: '' });
  const [matchSubsOverwritePositions, setMatchSubsOverwritePositions] = useState(false);
  const [cropTarget, setCropTarget] = useState('underlozhka');
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [underGroup, setUnderGroup] = useState('pattern');
  const [customUrl, setCustomUrl] = useState('');
  const [customFileName, setCustomFileName] = useState('');
  const [cropSource, setCropSource] = useState('');
  const [cropFileName, setCropFileName] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const [logoCaption, setLogoCaption] = useState('');
  const [title, setTitle] = useState(() => defaultTitleForPictureType(PICTURE_TYPE_TABLE));
  const [broadcastTournamentTitle, setBroadcastTournamentTitle] = useState('');
  const [titleColor, setTitleColor] = useState('');
  const [footerColor, setFooterColor] = useState('');
  const [footerVkEnabled, setFooterVkEnabled] = useState(true);
  const [footerVkText, setFooterVkText] = useState('');
  const [footerWebEnabled, setFooterWebEnabled] = useState(true);
  const [footerWebText, setFooterWebText] = useState('');
  const [footerIgEnabled, setFooterIgEnabled] = useState(true);
  const [footerIgText, setFooterIgText] = useState('');
  const [accent, setAccent] = useState('');
  const [tableBackground, setTableBackground] = useState('');
  const [leagueBarBackground, setLeagueBarBackground] = useState('');
  const [stadiumBackground, setStadiumBackground] = useState('');
  const [stylesCache, setStylesCache] = useState(() => emptyStylesCache());
  const [stylesLoading, setStylesLoading] = useState(false);
  const [stylesBusy, setStylesBusy] = useState(false);
  const [schemeName, setSchemeName] = useState('');
  const [activeStyleSource, setActiveStyleSource] = useState('');

  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [scale, setScale] = useState(1);
  const [frameHeight, setFrameHeight] = useState(0);
  const [frameWidth, setFrameWidth] = useState(DEFAULT_CANVAS_WIDTH);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [previewLightbox, setPreviewLightbox] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [selectedSponsorId, setSelectedSponsorId] = useState(null);
  const [sponsorsBusy, setSponsorsBusy] = useState(false);
  const [sponsorsCache, setSponsorsCache] = useState(() => emptySponsorsCache());
  const [sponsorsLoading, setSponsorsLoading] = useState(false);
  const [sponsorPickKey, setSponsorPickKey] = useState('');
  const [sponsorSchemeName, setSponsorSchemeName] = useState('');
  const [activeSponsorSource, setActiveSponsorSource] = useState('');

  const previewShellRef = useRef(null);
  const previewPanelRef = useRef(null);
  const settingsRef = useRef(null);
  const iframeRef = useRef(null);
  const frameHeightPrevRef = useRef(0);

  const solidUnderlozhki = catalog.solid || [];
  const patternUnderlozhki = catalog.pattern || [];
  const allUnderlozhki = catalog.all || [];

  const selectedUnderlozhka = useMemo(
    () => allUnderlozhki.find((item) => item.key === theme)
      || solidUnderlozhki[0]
      || allUnderlozhki[0]
      || null,
    [allUnderlozhki, solidUnderlozhki, theme]
  );

  const paletteUnderlozhki = solidUnderlozhki.length ? solidUnderlozhki : allUnderlozhki;

  const isMatchListPicture = isMatchListPictureType(pictureType);
  const isResultsPicture = pictureType === PICTURE_TYPE_RESULTS;
  const isGroupTablesPicture = pictureType === PICTURE_TYPE_GROUP_TABLES;
  const isTopStatsPicture = pictureType === PICTURE_TYPE_TOP10 || pictureType === PICTURE_TYPE_CARDS;
  const isCalendarPicture = pictureType === PICTURE_TYPE_CALENDAR;
  const isAfishaPicture = pictureType === PICTURE_TYPE_AFISHA;
  const isPlayoffPicture = pictureType === PICTURE_TYPE_PLAYOFF;
  const isPreviewPicture = pictureType === PICTURE_TYPE_PREVIEW;
  const isAnnouncementPicture = pictureType === PICTURE_TYPE_ANNOUNCEMENT;
  const isQuotePicture = pictureType === PICTURE_TYPE_QUOTE;
  const isAchievementsPicture = pictureType === PICTURE_TYPE_ACHIEVEMENTS;
  const isMatchCoverGoalPicture = isMatchCoverGoalPictureType(pictureType);
  const isBroadcastPreviewPicture = isBroadcastPreviewPictureType(pictureType);
  const isTourTeamPicture = isTourTeamPictureType(pictureType);
  const isMatchStatsPicture = pictureType === PICTURE_TYPE_MATCH_STATS;
  const isMatchRosterPicture = isMatchRosterPictureType(pictureType);
  const isMatchLineupsPicture = pictureType === PICTURE_TYPE_MATCH_LINEUPS;
  const isMvpPicture = pictureType === PICTURE_TYPE_MVP;
  const isSingleMatchPicture = isSingleMatchPictureType(pictureType);

  const matchStatsBarSourceLabel = (source) => {
    if (source === 'override') return t('igMatchStatsBarSourceOverride');
    if (source === 'team') return t('igMatchStatsBarSourceTeam');
    if (source === 'default') return t('igMatchStatsBarSourceDefault');
    return '';
  };

  const formState = useMemo(() => ({
    pictureType,
    typesCatalog: pictureTypes,
    leagueId: selectedLeague?.value || null,
    seasonId: selectedSeason?.value || null,
    stageId: selectedStage?.value || null,
    dateFrom: scheduleDateFrom,
    dateTo: scheduleDateTo,
    date: isAfishaPicture ? afishaDate : undefined,
    code: isAfishaPicture ? afishaCode : undefined,
    matchId: isSingleMatchPicture ? (selectedPreviewMatch?.value || null) : undefined,
    headline: (isSingleMatchPicture || isMvpPicture) ? title : undefined,
    tournamentTitle: isBroadcastPreviewPicture
      ? (broadcastTournamentTitle || undefined)
      : undefined,
    text: isAnnouncementPicture ? title : undefined,
    teamId: isQuotePicture
      ? (selectedQuoteTeam?.value || null)
      : (isMvpPicture ? (selectedMvpTeam?.value || null) : undefined),
    playerId: isMvpPicture
      ? (mvpPlayer?.id || mvpPlayer?.player_id || null)
      : undefined,
    personName: isQuotePicture
      ? quotePersonName
      : (isAchievementsPicture ? achievementName : undefined),
    quote: isQuotePicture ? quoteText : undefined,
    achievementTitle: isAchievementsPicture ? title : undefined,
    leftPhoto: isPreviewPicture ? previewLeftPhoto : undefined,
    rightPhoto: isPreviewPicture ? previewRightPhoto : undefined,
    includeUpcoming: isResultsPicture ? includeUpcoming : false,
    // omit = все; [] = пусто; [ids] = только указанные (контракт schedule/results)
    activeMatches: (() => {
      if (!isMatchListPicture || !matchListCandidates.length) return undefined;
      const allIds = matchListCandidates.map((item) => item.id);
      const selectedSet = new Set(matchListSelectedIds);
      const allSelected = allIds.length > 0
        && allIds.every((id) => selectedSet.has(id))
        && matchListSelectedIds.length === allIds.length;
      if (allSelected) return undefined;
      return matchListSelectedIds;
    })(),
    verticalLayout: isPlayoffPicture ? playoffVerticalLayout : undefined,
    trackKeys: isPlayoffPicture && playoffSelectedTrackKeys.length
      ? playoffSelectedTrackKeys
      : undefined,
    stageIds: isPlayoffPicture && playoffSelectedStageIds.length
      ? playoffSelectedStageIds
      : undefined,
    teamIds: isPlayoffPicture && playoffSelectedTeamIds.length
      ? playoffSelectedTeamIds
      : undefined,
    tourTeamId: isTourTeamPicture ? (selectedTourTeam?.value || null) : undefined,
    homeBarColor: isMatchStatsPicture ? matchStatsHomeBarColor : undefined,
    guestBarColor: isMatchStatsPicture ? matchStatsGuestBarColor : undefined,
    matchSubsStarters: isMatchRosterPicture && matchSubsManual
      ? matchSubsRoster.starters
      : undefined,
    matchSubsBench: isMatchRosterPicture && matchSubsManual
      ? matchSubsRoster.bench
      : undefined,
    matchSubsUseDbDefaults: isMatchRosterPicture
      ? (matchSubsManual ? false : true)
      : undefined,
    positionOverrides: isMatchRosterPicture && (matchSubsManual || matchSubsEditPositions)
      ? buildMatchSubsPositionOverrides(flattenMatchSubsRosterRows(matchSubsRoster))
      : undefined,
    overwritePositions: isMatchRosterPicture && (matchSubsManual || matchSubsEditPositions)
      ? matchSubsOverwritePositions
      : undefined,
    groups: (isGroupTablesPicture || (isTopStatsPicture && splitByGroups))
      ? selectedGroupNames
      : undefined,
    splitByGroups: isTopStatsPicture ? splitByGroups : undefined,
    columns: isCalendarPicture ? calendarColumns : undefined,
    groupStagesOnly: isCalendarPicture ? groupStagesOnly : false,
    customBg: {
      theme,
      url: customUrl,
      // Custom underlozhka requires accent (backend falls back to blue otherwise)
      accent: accent || (customUrl ? (selectedUnderlozhka?.accent || '') : ''),
      tableBackground: isMatchCoverGoalPicture ? undefined : tableBackground,
      leagueBarBackground: isMatchCoverGoalPicture ? undefined : leagueBarBackground,
      stadiumBackground: isMatchCoverGoalPicture ? stadiumBackground : undefined,
      logoCaption: isMvpPicture ? undefined : logoCaption,
      title: (isMatchCoverGoalPicture || isBroadcastPreviewPicture || isMvpPicture)
        ? undefined
        : title,
      titleColor: isMatchCoverGoalPicture ? undefined : titleColor,
      footerColor,
      footer: {
        vk: { enabled: footerVkEnabled, text: footerVkText },
        web: { enabled: footerWebEnabled, text: footerWebText },
        instagram: { enabled: footerIgEnabled, text: footerIgText },
      },
    },
  }), [
    pictureType,
    pictureTypes,
    selectedLeague,
    selectedSeason,
    selectedStage,
    scheduleDateFrom,
    scheduleDateTo,
    afishaDate,
    afishaCode,
    isAfishaPicture,
    selectedPreviewMatch,
    isSingleMatchPicture,
    isPreviewPicture,
    isAnnouncementPicture,
    selectedQuoteTeam,
    quotePersonName,
    quoteText,
    isQuotePicture,
    achievementName,
    isAchievementsPicture,
    isMatchCoverGoalPicture,
    isBroadcastPreviewPicture,
    broadcastTournamentTitle,
    isTourTeamPicture,
    selectedTourTeam,
    isMatchStatsPicture,
    matchStatsHomeBarColor,
    matchStatsGuestBarColor,
    isMatchRosterPicture,
    matchSubsManual,
    matchSubsEditPositions,
    matchSubsRoster,
    matchSubsOverwritePositions,
    isMvpPicture,
    mvpPlayer,
    selectedMvpTeam,
    previewLeftPhoto,
    previewRightPhoto,
    includeUpcoming,
    isResultsPicture,
    isMatchListPicture,
    matchListCandidates,
    matchListSelectedIds,
    playoffVerticalLayout,
    isPlayoffPicture,
    playoffSelectedTrackKeys,
    playoffSelectedStageIds,
    playoffSelectedTeamIds,
    selectedGroupNames,
    isGroupTablesPicture,
    isTopStatsPicture,
    splitByGroups,
    calendarColumns,
    groupStagesOnly,
    isCalendarPicture,
    theme,
    customUrl,
    accent,
    tableBackground,
    leagueBarBackground,
    stadiumBackground,
    logoCaption,
    title,
    titleColor,
    footerColor,
    footerVkEnabled,
    footerVkText,
    footerWebEnabled,
    footerWebText,
    footerIgEnabled,
    footerIgText,
    selectedUnderlozhka,
  ]);

  const canPreview = useMemo(() => {
    if (!formState.leagueId && !isSingleMatchPicture) return false;
    if (isMatchCoverGoalPicture) {
      return Boolean(
        formState.leagueId
        && selectedStage?.value
        && selectedPreviewMatch?.value
      );
    }
    if (isSingleMatchPicture) {
      return Boolean(selectedPreviewMatch?.value);
    }
    if (!formState.leagueId) return false;
    if (isMatchListPicture) {
      if (scheduleFilterMode === 'stage') {
        return Boolean(selectedStage?.value);
      }
      return Boolean(scheduleDateFrom && scheduleDateTo);
    }
    if (isAfishaPicture) {
      return Boolean(formState.seasonId && afishaDate);
    }
    if (isQuotePicture) {
      return Boolean(formState.seasonId && selectedQuoteTeam?.value);
    }
    if (isTourTeamPicture) {
      return Boolean(formState.seasonId && selectedTourTeam?.value);
    }
    if (isMvpPicture) {
      return Boolean(
        formState.seasonId
        && (mvpPlayer?.id || mvpPlayer?.player_id)
        && selectedMvpTeam?.value
      );
    }
    return Boolean(formState.seasonId);
  }, [
    formState.leagueId,
    formState.seasonId,
    isMatchListPicture,
    isAfishaPicture,
    isSingleMatchPicture,
    isMatchCoverGoalPicture,
    isQuotePicture,
    isTourTeamPicture,
    selectedTourTeam,
    isMvpPicture,
    mvpPlayer,
    selectedMvpTeam,
    scheduleFilterMode,
    selectedStage,
    scheduleDateFrom,
    scheduleDateTo,
    afishaDate,
    selectedPreviewMatch,
    selectedQuoteTeam,
  ]);

  const matchListDayGroups = useMemo(() => {
    if (!matchListCandidates.length) return [];
    const byId = new Map(matchListCandidates.map((item) => [item.id, item]));
    const used = new Set();
    const groups = [];
    (matchListDays || []).forEach((day, index) => {
      const ids = Array.isArray(day?.match_ids) ? day.match_ids : [];
      const matches = ids
        .map((id) => byId.get(Number(id)))
        .filter(Boolean);
      if (!matches.length) return;
      matches.forEach((item) => used.add(item.id));
      const label = [day.dateLabel || day.date, day.weekday, day.stadium]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' · ');
      groups.push({
        key: `day-${day.date || index}-${day.stadiumName || day.stadium || index}`,
        label: label || null,
        matches,
      });
    });
    const rest = matchListCandidates.filter((item) => !used.has(item.id));
    if (rest.length) {
      groups.push({ key: 'day-rest', label: null, matches: rest });
    }
    return groups.length ? groups : [{ key: 'day-all', label: null, matches: matchListCandidates }];
  }, [matchListCandidates, matchListDays]);

  const playoffMirrorLocked = Boolean(
    isPlayoffPicture
    && (
      !playoffAllowMirror
      || playoffSelectedTeamIds.length > 0
      || (
        playoffSelectedStageIds.length > 0
        && !playoffSelectionHasFinal(playoffSelectedStageIds, playoffAvailableStages)
      )
    )
  );

  useEffect(() => {
    if (!isPlayoffPicture || !playoffMirrorLocked) return;
    if (!playoffVerticalLayout) setPlayoffVerticalLayout(true);
  }, [isPlayoffPicture, playoffMirrorLocked, playoffVerticalLayout]);

  const isCustomBg = Boolean(customUrl);
  const cropAspect = useMemo(() => {
    if (cropTarget === 'previewLeft' || cropTarget === 'previewRight') {
      return PREVIEW_PHOTO_ASPECT;
    }
    return getUnderlozhkaAspect(frameHeight, frameWidth);
  }, [cropTarget, frameHeight, frameWidth]);
  const cropOutputWidth = (cropTarget === 'previewLeft' || cropTarget === 'previewRight')
    ? PREVIEW_PHOTO_SLOT.width
    : (frameWidth || DEFAULT_CANVAS_WIDTH);
  const titlePlaceholder = defaultTitleForPictureType(pictureType);

  const sponsorPool = useMemo(
    () => (sponsorsCache.pool || []).map(normalizePoolItem).filter((item) => item.key && item.src),
    [sponsorsCache.pool]
  );

  const sponsorPickOptions = useMemo(
    () => sponsorPool.map((item) => ({
      value: item.key,
      name: item.name || item.key,
      src: item.src,
    })),
    [sponsorPool]
  );

  const pictureTypeOptions = useMemo(() => {
    const otherKeys = new Set([
      PICTURE_TYPE_ANNOUNCEMENT,
      PICTURE_TYPE_QUOTE,
      PICTURE_TYPE_ACHIEVEMENTS,
      PICTURE_TYPE_MVP,
    ]);
    const byTournament = [];
    const byMatch = [];
    const byOther = [];
    pictureTypes.forEach((item, index) => {
      const option = {
        value: item.key,
        name: (lang === 'en' && item.labelEn ? item.labelEn : item.label) || item.key,
        key: index,
      };
      if (isSingleMatchPictureType(item.key)) byMatch.push(option);
      else if (otherKeys.has(item.key)) byOther.push(option);
      else byTournament.push(option);
    });
    return [
      byTournament.length
        ? { type: 'group', name: t('igPictureGroupTournament'), items: byTournament }
        : null,
      byMatch.length
        ? { type: 'group', name: t('igPictureGroupMatch'), items: byMatch }
        : null,
      byOther.length
        ? { type: 'group', name: t('igPictureGroupOther'), items: byOther }
        : null,
    ].filter(Boolean);
  }, [pictureTypes, lang, t]);

  const findByValue = (items, value) => items.find((item) => String(item.value) === String(value));

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, { keys: ['name'], threshold: 0.3 });
    return (value) => {
      if (!value.length) return options;
      return fuse.search(value).map((item) => item.item);
    };
  };

  /** Group-aware filter for picture type picker (react-select-search groups). */
  const filterPictureTypeOptions = (options) => (query) => {
    if (!query || !String(query).trim()) return options;
    const q = String(query).trim();
    return options
      .map((group) => {
        if (group?.type !== 'group' || !Array.isArray(group.items)) {
          return null;
        }
        const fuse = new Fuse(group.items, { keys: ['name'], threshold: 0.3 });
        const items = fuse.search(q).map((hit) => hit.item);
        return items.length ? { ...group, items } : null;
      })
      .filter(Boolean);
  };

  const mapSelectOptions = (items, getValue, getName) => (
    items
      .map((item, index) => {
        const value = Number(getValue(item));
        return {
          ...item,
          value,
          name: getName(item) || '',
          key: index,
        };
      })
      .filter((item) => item.value > 0)
  );

  const mapSeasons = (items) => mapSelectOptions(
    extractTournamentList(items),
    (item) => item.season_id || item.id,
    (item) => item.season_name || item.name
  );

  const resetMatchListPicker = () => {
    setMatchListCandidates([]);
    setMatchListDays([]);
    setMatchListSelectedIds([]);
    setMatchListLoading(false);
  };

  const resetScheduleFilters = () => {
    setScheduleFilterMode('stage');
    setStages([]);
    setSelectedStage(null);
    setScheduleDateFrom('');
    setScheduleDateTo('');
    setIncludeUpcoming(true);
    resetMatchListPicker();
  };

  const resetGroupTableFilters = () => {
    setSeasonGroups([]);
    setSelectedGroupNames([]);
    setSplitByGroups(true);
  };

  const resetCalendarFilters = () => {
    setCalendarColumns(3);
    setGroupStagesOnly(false);
  };

  const resetAfishaFilters = () => {
    setAfishaDate('');
    setAfishaCode('');
  };

  const resetPlayoffFilters = () => {
    setPlayoffVerticalLayout(false);
    setPlayoffAvailableTracks([]);
    setPlayoffAvailableStages([]);
    setPlayoffAvailableTeams([]);
    setPlayoffSelectedTrackKeys([]);
    setPlayoffSelectedStageIds([]);
    setPlayoffSelectedTeamIds([]);
    setPlayoffAllowMirror(true);
  };

  const resetMatchStatsBarColors = () => {
    setMatchStatsHomeBarColor('');
    setMatchStatsGuestBarColor('');
    setMatchStatsBarMeta(null);
  };

  const resetMatchSubsFilters = () => {
    setMatchSubsManual(false);
    setMatchSubsEditPositions(false);
    setMatchSubsRoster(createEmptyMatchSubsRoster());
    setMatchSubsDbRoster(null);
    setMatchSubsTeamNames({ home: '', guest: '' });
    setMatchSubsOverwritePositions(false);
  };

  const updateMatchSubsPlayer = (block, side, key, field, value) => {
    setMatchSubsRoster((prev) => ({
      ...prev,
      [block]: {
        ...prev[block],
        [side]: (prev[block]?.[side] || []).map((row) => (
          row.key === key ? { ...row, [field]: value } : row
        )),
      },
    }));
  };

  const applyMatchSubsPlayerPick = (block, side, key, player) => {
    const fields = playerFieldsFromSearchPick(player);
    if (!fields) return;
    setMatchSubsRoster((prev) => ({
      ...prev,
      [block]: {
        ...prev[block],
        [side]: (prev[block]?.[side] || []).map((row) => (
          row.key === key
            ? {
              ...row,
              ...fields,
              // Keep row position if picker has none
              position: fields.position || row.position || '',
            }
            : row
        )),
      },
    }));
    setPreviewHtml('');
    setFrameHeight(0);
    setMessage({ text: '', type: '' });
  };

  const addMatchSubsPlayer = (block, side) => {
    setMatchSubsRoster((prev) => {
      const current = prev[block]?.[side] || [];
      return {
        ...prev,
        [block]: {
          ...prev[block],
          [side]: [...current, createMatchSubsPlayerRow()],
        },
      };
    });
  };

  const removeMatchSubsPlayer = (block, side, key) => {
    setMatchSubsRoster((prev) => {
      const current = prev[block]?.[side] || [];
      const next = current.filter((row) => row.key !== key);
      return {
        ...prev,
        [block]: {
          ...prev[block],
          [side]: next.length ? next : [createMatchSubsPlayerRow()],
        },
      };
    });
  };

  const enableMatchSubsManual = (enabled) => {
    setMatchSubsManual(enabled);
    if (enabled) {
      setMatchSubsEditPositions(false);
      setMatchSubsRoster((prev) => (
        matchSubsDbRoster
          ? mergeMatchSubsRosterPositions(seedMatchSubsRoster(matchSubsDbRoster), prev)
          : (flattenMatchSubsRosterRows(prev).some((row) => (
            row.player_id || row.family || row.name || row.player_number
          )) ? prev : createEmptyMatchSubsRoster())
      ));
    }
    setPreviewHtml('');
    setFrameHeight(0);
    setMessage({ text: '', type: '' });
  };

  const enableMatchSubsPositionEditor = (enabled) => {
    setMatchSubsEditPositions(enabled);
    if (enabled && matchSubsDbRoster) {
      setMatchSubsRoster((prev) => (
        mergeMatchSubsRosterPositions(
          seedMatchSubsRoster(matchSubsDbRoster, { positionsAsOverrides: true }),
          prev
        )
      ));
    }
    setPreviewHtml('');
    setFrameHeight(0);
    setMessage({ text: '', type: '' });
  };

  const resetPreviewFilters = () => {
    setPreviewMatches([]);
    setSelectedPreviewMatch(null);
    setPreviewLeftPhoto('');
    setPreviewRightPhoto('');
    setPreviewLeftFileName('');
    setPreviewRightFileName('');
    resetMatchStatsBarColors();
    resetMatchSubsFilters();
  };

  const resetQuoteFilters = () => {
    setQuoteTeams([]);
    setSelectedQuoteTeam(null);
    setQuotePersonName('');
    setQuoteText('');
  };

  const resetAchievementsFilters = () => {
    setAchievementName('');
  };

  const resetTourTeamFilters = () => {
    setTourTeams([]);
    setSelectedTourTeam(null);
  };

  const resetMvpFilters = () => {
    setMvpPlayer(null);
    setSelectedMvpTeam(null);
  };

  const applyMvpPlayer = (player) => {
    setMvpPlayer(player || null);
    if (!player) {
      setSelectedMvpTeam(null);
      return;
    }
    const teamId = resolvePlayerTeamId(player);
    if (!teamId) {
      setSelectedMvpTeam(null);
      return;
    }
    const fromList = quoteTeams.find((item) => Number(item.value) === Number(teamId));
    setSelectedMvpTeam(
      fromList || {
        value: teamId,
        name: String(player.team_name || player.team?.name || `#${teamId}`),
      }
    );
  };

  const openTourTeamEditor = () => {
    const params = new URLSearchParams();
    if (selectedCountry?.value) params.set('country', String(selectedCountry.value));
    if (selectedLeague?.value) params.set('league', String(selectedLeague.value));
    if (selectedSeason?.value) params.set('season', String(selectedSeason.value));
    const tourId = Number(
      selectedTourTeam?.stage_id
      || selectedTourTeam?.stageId
      || selectedStage?.value
    );
    if (Number.isFinite(tourId) && tourId > 0) params.set('tour', String(tourId));
    const qs = params.toString();
    history.push(qs ? `/league_tour_team?${qs}` : '/league_tour_team');
  };

  const loadTourTeams = async (leagueId, seasonId) => {
    if (!leagueId || !seasonId) {
      setTourTeams([]);
      return [];
    }
    const body = { season_id: Number(seasonId) };
    const tryEndpoints = [
      `/leagues/${leagueId}/getTourTeams`,
      `/leagues/${leagueId}/getLeagueTourTeams`,
    ];
    let lastError = null;
    for (let i = 0; i < tryEndpoints.length; i += 1) {
      try {
        const response = await authorizedPost(dispatch, tryEndpoints[i], {
          ...body,
          league_id: Number(leagueId),
        });
        const payload = response.data;
        let list = extractTournamentList(payload);
        if (!list.length && payload && typeof payload === 'object') {
          const nested = payload.tour_teams || payload.teams || payload.items
            || payload.data?.tour_teams || payload.data?.teams || payload.data?.items;
          if (Array.isArray(nested)) list = nested;
        }
        const next = list
          .map((item, index) => {
            const id = resolveTourTeamId(item);
            if (!id) return null;
            const stageName = String(item.stage_name || item.tour_name || item.name || '').trim();
            const label = stageName || `${t('igTourTeamFallback')} #${id}`;
            return {
              ...item,
              value: id,
              name: label,
              key: index,
              stage_id: item.stage_id || item.stageId || null,
            };
          })
          .filter(Boolean);
        setTourTeams(next);
        return next;
      } catch (error) {
        lastError = error;
      }
    }
    setTourTeams([]);
    setMessage({
      text: getApiErrorMessage(lastError, t('igTourTeamsError')),
      type: 'error',
    });
    return [];
  };

  const loadQuoteTeams = async (leagueId, seasonId) => {
    if (!leagueId || !seasonId || !champId) {
      setQuoteTeams([]);
      return [];
    }
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminSeasonTeams, {
        ...champFields(champId),
        league_id: Number(leagueId),
        season_id: Number(seasonId),
      });
      const payload = response.data;
      const list = Array.isArray(payload?.teams) ? payload.teams : extractTournamentList(payload);
      const next = list
        .map((item) => {
          const team = normalizeSeasonTeam(item);
          const id = team.team_id || item.id || item.team_id;
          const name = team.name || item.name || '';
          if (!id || !name) return null;
          return { value: Number(id), name: String(name) };
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
      setQuoteTeams(next);
      return next;
    } catch (error) {
      setQuoteTeams([]);
      setMessage({ text: getApiErrorMessage(error, t('igQuoteTeamsError')), type: 'error' });
      return [];
    }
  };

  const formatPreviewMatchOption = (match) => {
    const teamLabel = (team, fallbackName, fallbackShort) => {
      const short = String(
        team?.short_name
        || fallbackShort
        || ''
      ).trim();
      const name = String(team?.name || fallbackName || '').trim();
      if (short) return short;
      if (name.length > 22) return `${name.slice(0, 21)}…`;
      return name || '?';
    };
    const home = teamLabel(
      match?.home_team,
      match?.home_team_name,
      match?.home_team_short_name
    );
    const guest = teamLabel(
      match?.guest_team,
      match?.guest_team_name,
      match?.guest_team_short_name
    );
    const kickoff = formatKickoff(match);
    if (kickoff && kickoff !== '—') return `${kickoff} · ${home} — ${guest}`;
    return `${home} — ${guest}`;
  };

  const loadPreviewMatches = async (leagueId, seasonId, stageId, { onlyMine } = {}) => {
    if (!leagueId || !seasonId || !stageId || !champId) {
      setPreviewMatches([]);
      return [];
    }
    const filterMine = onlyMine != null
      ? Boolean(onlyMine)
      : (canUseMyStaffMatches && matchListOnlyMine);
    try {
      const response = await authorizedPost(
        dispatch,
        API_URLS.getAdminMatches,
        matchRequestBody(champId, {
          league_id: Number(leagueId),
          season_id: Number(seasonId),
          stage_id: Number(stageId),
          limit: 200,
          offset: 0,
          ...(filterMine ? { my_games: true } : {}),
        })
      );
      const page = extractMatchesPage(response.data);
      const next = mapSelectOptions(
        page.matches || [],
        (item) => item.id || item.match_id,
        formatPreviewMatchOption
      );
      setPreviewMatches(next);
      return next;
    } catch (error) {
      setPreviewMatches([]);
      setMessage({ text: getApiErrorMessage(error, t('igPreviewMatchesError')), type: 'error' });
      return [];
    }
  };

  const loadSeasonGroups = async (leagueId, seasonId, { preselect = true } = {}) => {
    if (!leagueId || !seasonId || !champId) {
      setSeasonGroups([]);
      setSelectedGroupNames([]);
      return [];
    }
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminSeasonGroups, {
        ...champFields(champId),
        league_id: Number(leagueId),
        season_id: Number(seasonId),
      });
      const derived = deriveGroupsFromServer(response.data);
      const nextGroups = [...(derived.list || [])]
        .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0))
        .map((group, index) => ({
          id: group.serverId ?? group.id ?? index + 1,
          name: String(group.name || '').trim(),
          priority: Number(group.priority) || index + 1,
        }))
        .filter((group) => group.name);
      setSeasonGroups(nextGroups);
      // Group tables: preselect up to 4. Top-10/cards: empty = all groups on server.
      setSelectedGroupNames(
        preselect ? nextGroups.slice(0, 4).map((group) => group.name) : []
      );
      return nextGroups;
    } catch (error) {
      setSeasonGroups([]);
      setSelectedGroupNames([]);
      setMessage({ text: getApiErrorMessage(error, t('igGroupTablesEmpty')), type: 'error' });
      return [];
    }
  };

  const loadStagesForSeason = async (leagueId, seasonId) => {
    if (!leagueId || !seasonId) {
      setStages([]);
      return [];
    }
    try {
      const response = await authorizedPost(dispatch, `/leagues/${leagueId}/getLeagueStages`, {
        league_id: Number(leagueId),
        season_id: Number(seasonId),
      });
      const nextStages = mapSelectOptions(
        extractTournamentList(response.data),
        (item) => item.id || item.stage_id,
        (item) => item.name || item.stage_name
      );
      setStages(nextStages);
      return nextStages;
    } catch (error) {
      setStages([]);
      setMessage({ text: getApiErrorMessage(error, t('tsLoadStagesError')), type: 'error' });
      return [];
    }
  };

  const resetCascade = () => {
    setSelectedCountry(null);
    setSelectedLeague(null);
    setSelectedSeason(null);
    setLeagues([]);
    setSeasons([]);
    resetScheduleFilters();
    resetGroupTableFilters();
    resetCalendarFilters();
    resetAfishaFilters();
    resetPreviewFilters();
    resetPlayoffFilters();
    resetQuoteFilters();
    resetAchievementsFilters();
    resetTourTeamFilters();
    resetMvpFilters();
    resetMatchSubsFilters();
    setPreviewHtml('');
    setSponsors([]);
    setSelectedSponsorId(null);
    setActiveSponsorSource('');
    setSponsorPickKey('');
    setSponsorsCache(emptySponsorsCache());
    setStylesCache(emptyStylesCache());
    setActiveStyleSource('');
    setTitle(defaultTitleForPictureType(pictureType));
    setMessage({ text: '', type: '' });
  };

  const loadCountries = async () => {
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountries, champFields(champId));
      const nextCountries = mapSelectOptions(
        extractTournamentList(response.data),
        (item) => item.id,
        (item) => item.name
      );
      setCountries(nextCountries);
      return nextCountries;
    } catch (error) {
      setCountries([]);
      setMessage({ text: getApiErrorMessage(error, t('tsLoadCountriesError')), type: 'error' });
      return [];
    }
  };

  const loadLeaguesForCountry = async (countryId) => {
    if (!countryId) {
      setLeagues([]);
      return [];
    }
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(countryId) });
      const nextLeagues = mapSelectOptions(
        extractTournamentList(extractEntity(response.data).leagues),
        (item) => item.id || item.league_id,
        (item) => item.name || item.league_name
      ).map((item) => ({
        ...item,
        seasons: extractTournamentList(item.seasons),
      }));
      setLeagues(nextLeagues);
      return nextLeagues;
    } catch (error) {
      setLeagues([]);
      setMessage({ text: getApiErrorMessage(error, t('tsLoadLeaguesError')), type: 'error' });
      return [];
    }
  };

  const loadSeasonsForLeague = async (league) => {
    if (!league) {
      setSeasons([]);
      return [];
    }
    let nextSeasons = mapSeasons(league.seasons);
    if (!nextSeasons.length) {
      try {
        const response = await authorizedPost(dispatch, API_URLS.getAdminLeague, { id: Number(league.value) });
        nextSeasons = mapSeasons(extractEntity(response.data).seasons);
      } catch (error) {
        nextSeasons = [];
        setMessage({ text: getApiErrorMessage(error, t('tsLoadSeasonsError')), type: 'error' });
      }
    }
    setSeasons(nextSeasons);
    return nextSeasons;
  };

  useEffect(() => {
    let cancelled = false;
    fetchUnderlozhkiCatalog(dispatch).then((nextCatalog) => {
      if (cancelled) return;
      setCatalog(nextCatalog);
      if (nextCatalog.error) {
        setMessage({ text: t('igUnderlozhkiError'), type: 'error' });
      }
      const keys = (nextCatalog.all || []).map((item) => item.key);
      if (keys.length) {
        setTheme((current) => {
          if (keys.includes(current)) return current;
          const patternKeys = (nextCatalog.pattern || []).map((item) => item.key);
          return patternKeys[0] || keys[0];
        });
      }
      setUnderGroup((current) => {
        if (current === 'solid' || current === 'pattern') return current;
        return 'pattern';
      });
    });
    fetchV2PictureTypes(dispatch).then((types) => {
      if (cancelled) return;
      setPictureTypes(types);
      setPictureType((current) => {
        const next = types.some((item) => item.key === current)
          ? current
          : (types[0]?.key || PICTURE_TYPE_TABLE);
        if (next !== current) {
          setTitle(defaultTitleForPictureType(next));
        }
        return next;
      });
    });
    return () => { cancelled = true; };
    // `t` is stable via useI18n; keep out of deps so catalog load is not re-fired on parent renders
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      resetCascade();
      if (!champId) {
        setCountries([]);
        return;
      }
      await loadCountries();
      if (cancelled) return;
    };
    init();
    return () => { cancelled = true; };
  }, [champId]);

  useEffect(() => {
    if (!canUseMyStaffMatches) {
      setMatchListOnlyMine(false);
      return;
    }
    setMatchListOnlyMine(preferMyStaffMatchesDefault);
  }, [canUseMyStaffMatches, preferMyStaffMatchesDefault]);

  useEffect(() => {
    if (!isMatchListPicture || !canPreview) return undefined;

    let cancelled = false;
    setMatchListLoading(true);

    const timer = setTimeout(() => {
      fetchMatchListCandidates(dispatch, {
        pictureType: formState.pictureType,
        typesCatalog: formState.typesCatalog,
        leagueId: formState.leagueId,
        seasonId: formState.seasonId,
        stageId: formState.stageId,
        dateFrom: formState.dateFrom,
        dateTo: formState.dateTo,
        includeUpcoming: formState.includeUpcoming,
      })
        .then((result) => {
          if (cancelled) return;
          const matches = Array.isArray(result?.matches) ? result.matches : [];
          setMatchListCandidates(matches);
          setMatchListDays(Array.isArray(result?.days) ? result.days : []);
          setMatchListSelectedIds(matches.map((item) => item.id));
        })
        .catch((error) => {
          if (cancelled) return;
          setMatchListCandidates([]);
          setMatchListDays([]);
          setMatchListSelectedIds([]);
          setMessage({
            text: getSocialPictureErrorMessage(error, t, t('igMatchListError')),
            type: 'error',
          });
        })
        .finally(() => {
          if (!cancelled) setMatchListLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    isMatchListPicture,
    canPreview,
    dispatch,
    formState.leagueId,
    formState.seasonId,
    formState.stageId,
    formState.dateFrom,
    formState.dateTo,
    formState.includeUpcoming,
    formState.pictureType,
    formState.typesCatalog,
  ]);

  useEffect(() => {
    if (!canPreview) {
      setPreviewHtml('');
      setPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setMessage((prev) => (prev.type === 'error' && prev.text === t('igUnderlozhkiError') ? prev : { text: '', type: '' }));

    const timer = setTimeout(() => {
      fetchSocialPictureHtml(dispatch, formState)
        .then((result) => {
          if (cancelled) return;
          const nextWidth = Number(result?.canvasWidth) > 0
            ? Number(result.canvasWidth)
            : getCanvasWidthForPictureType(formState.pictureType);
          setFrameWidth(nextWidth);
          if (Number(result?.canvasHeight) > 0) {
            setFrameHeight(Number(result.canvasHeight));
          } else if (
            formState.pictureType === PICTURE_TYPE_PREVIEW
            || formState.pictureType === PICTURE_TYPE_QUOTE
            || formState.pictureType === PICTURE_TYPE_ACHIEVEMENTS
            || formState.pictureType === PICTURE_TYPE_MVP
          ) {
            setFrameHeight(
              formState.pictureType === PICTURE_TYPE_QUOTE
                ? QUOTE_CANVAS_SIZE
                : (formState.pictureType === PICTURE_TYPE_ACHIEVEMENTS
                  ? ACHIEVEMENTS_CANVAS_SIZE
                  : (formState.pictureType === PICTURE_TYPE_MVP
                    ? MVP_CANVAS_SIZE
                    : PREVIEW_CANVAS_SIZE))
            );
          } else if (
            formState.pictureType === PICTURE_TYPE_MATCH_COVER_GOAL
            || formState.pictureType === PICTURE_TYPE_BROADCAST_PREVIEW
          ) {
            setFrameHeight(MATCH_COVER_CANVAS_HEIGHT);
          } else if (formState.pictureType === PICTURE_TYPE_TOUR_TEAM) {
            setFrameHeight(TOUR_TEAM_CANVAS_HEIGHT);
          }
          if (formState.pictureType === PICTURE_TYPE_PLAYOFF) {
            if (Array.isArray(result?.availableTracks)) {
              setPlayoffAvailableTracks((prev) => mergePlayoffAvailableTracks(
                prev,
                result.availableTracks
              ));
            }
            if (Array.isArray(result?.availableStages)) {
              // Keep empty rounds (matches === 0); merge so filtered previews don't shrink the picker
              setPlayoffAvailableStages((prev) => mergePlayoffAvailableStages(
                prev,
                result.availableStages
              ));
            }
            if (Array.isArray(result?.availableTeams)) {
              setPlayoffAvailableTeams(
                result.availableTeams.map(normalizePlayoffAvailableTeam).filter(Boolean)
              );
            }
            if (result?.selection && typeof result.selection === 'object') {
              setPlayoffAllowMirror(result.selection.allowMirror !== false);
              if (result.selection.allowMirror === false || result.selection.forcedVertical) {
                setPlayoffVerticalLayout(true);
              }
            } else if (result?.verticalLayout) {
              setPlayoffAllowMirror(false);
              setPlayoffVerticalLayout(true);
            } else {
              setPlayoffAllowMirror(true);
            }
          }
          if (formState.pictureType === PICTURE_TYPE_MATCH_STATS) {
            const barColors = result?.barColors && typeof result.barColors === 'object'
              ? result.barColors
              : null;
            const matchInfo = result?.match && typeof result.match === 'object'
              ? result.match
              : null;
            const homeTeam = matchInfo?.home_team || matchInfo?.homeTeam || null;
            const guestTeam = matchInfo?.guest_team || matchInfo?.guestTeam || null;
            setMatchStatsBarMeta({
              home: String(barColors?.home || '').trim(),
              guest: String(barColors?.guest || '').trim(),
              homeSource: String(barColors?.homeSource || '').trim(),
              guestSource: String(barColors?.guestSource || '').trim(),
              homeTeamColor: barColors?.homeTeamColor
                || homeTeam?.color
                || null,
              guestTeamColor: barColors?.guestTeamColor
                || guestTeam?.color
                || null,
              homeName: String(homeTeam?.short_name || homeTeam?.name || '').trim(),
              guestName: String(guestTeam?.short_name || guestTeam?.name || '').trim(),
            });
          }
          if (isMatchRosterPictureType(formState.pictureType)) {
            const matchInfo = result?.match && typeof result.match === 'object'
              ? result.match
              : null;
            const homeTeam = matchInfo?.home_team || matchInfo?.homeTeam || null;
            const guestTeam = matchInfo?.guest_team || matchInfo?.guestTeam || null;
            setMatchSubsTeamNames({
              home: String(homeTeam?.short_name || homeTeam?.name || t('igMatchSubsHome')).trim(),
              guest: String(guestTeam?.short_name || guestTeam?.name || t('igMatchSubsGuest')).trim(),
            });
            // Snapshot only — never write matchSubsRoster here (that re-triggers preview → loop)
            if (result?.roster && formState.matchSubsUseDbDefaults !== false) {
              setMatchSubsDbRoster(result.roster);
            }
          }
          setPreviewHtml(result?.html || '');
        })
        .catch((error) => {
          if (!cancelled) {
            setPreviewHtml('');
            setMessage({ text: getSocialPictureErrorMessage(error, t, t('igPreviewError')), type: 'error' });
          }
        })
        .finally(() => {
          if (!cancelled) setPreviewLoading(false);
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Intentionally omit `t`: translations must not cancel in-flight preview loads
  }, [canPreview, dispatch, formState]);

  useEffect(() => {
    const updateScale = () => {
      const shell = previewShellRef.current;
      const width = shell?.clientWidth || DEFAULT_CANVAS_WIDTH;
      // For wide canvases (chess 1440) scale against 1080 so preview stays readable and scrolls horizontally.
      const scaleBase = Math.min(frameWidth || DEFAULT_CANVAS_WIDTH, DEFAULT_CANVAS_WIDTH);
      const widthScale = Math.max(0.01, width / scaleBase);

      const isCompact = typeof window !== 'undefined'
        && window.matchMedia('(max-width: 1100px)').matches;
      if (isCompact && frameHeight > 0) {
        const availableHeight = Math.max(120, shell?.clientHeight || 0);
        const heightScale = availableHeight / frameHeight;
        setScale(Math.min(widthScale, heightScale));
        return;
      }

      // Wide: fill column width by 1080-base; taller/wider chess scrolls in shell.
      setScale(widthScale);
    };

    const onResize = () => updateScale();
    onResize();

    const shell = previewShellRef.current;
    const panel = previewPanelRef.current;
    const observers = [];
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(onResize);
      if (shell) observer.observe(shell);
      if (panel) observer.observe(panel);
      observers.push(observer);
    }

    window.addEventListener('resize', onResize);
    return () => {
      observers.forEach((observer) => observer.disconnect());
      window.removeEventListener('resize', onResize);
    };
  }, [frameHeight, frameWidth, previewCollapsed, previewLightbox]);

  useEffect(() => {
    if (!previewLightbox) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setPreviewLightbox(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [previewLightbox]);

  useEffect(() => {
    const resetCompactUi = () => {
      if (!window.matchMedia('(max-width: 1100px)').matches) {
        setPreviewCollapsed(false);
        setPreviewLightbox(false);
      }
    };
    window.addEventListener('resize', resetCompactUi);
    return () => window.removeEventListener('resize', resetCompactUi);
  }, []);

  useEffect(() => {
    const preview = previewPanelRef.current;
    const settings = settingsRef.current;
    if (!preview || !settings) return undefined;

    const syncSettingsHeight = () => {
      const isCompact = window.matchMedia('(max-width: 1100px)').matches;
      if (isCompact) {
        settings.style.height = '';
        return;
      }
      const nextHeight = Math.ceil(preview.getBoundingClientRect().height);
      if (nextHeight > 0) {
        settings.style.height = `${nextHeight}px`;
      } else {
        settings.style.height = '';
      }
    };

    syncSettingsHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncSettingsHeight);
      return () => {
        window.removeEventListener('resize', syncSettingsHeight);
        settings.style.height = '';
      };
    }

    const observer = new ResizeObserver(syncSettingsHeight);
    observer.observe(preview);
    window.addEventListener('resize', syncSettingsHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncSettingsHeight);
      settings.style.height = '';
    };
  }, [previewHtml, frameHeight, scale, previewLoading]);

  const syncIframeHeight = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      // Let the document size to content before measuring.
      iframe.style.height = 'auto';

      const page = doc.querySelector('.page');
      const measured = page
        ? Math.ceil(page.offsetHeight || page.getBoundingClientRect().height || 0)
        : Math.max(
          doc.body?.offsetHeight || 0,
          doc.documentElement?.offsetHeight || 0,
          doc.body?.scrollHeight || 0,
          doc.documentElement?.scrollHeight || 0
        );
      const measuredWidth = page
        ? Math.ceil(page.offsetWidth || page.getBoundingClientRect().width || 0)
        : 0;

      if (measured > 0) {
        iframe.style.height = `${measured}px`;
        setFrameHeight((prev) => (prev === measured ? prev : measured));
      }
      if (measuredWidth > 0) {
        setFrameWidth((prev) => (prev === measuredWidth ? prev : measuredWidth));
      }
    } catch (error) {
      // keep previous height
    }
  };

  useEffect(() => {
    if (!previewHtml) return undefined;
    const timers = [
      setTimeout(syncIframeHeight, 50),
      setTimeout(syncIframeHeight, 300),
      setTimeout(syncIframeHeight, 800),
    ];
    return () => timers.forEach(clearTimeout);
  }, [previewHtml]);

  useEffect(() => {
    if (!frameHeight) return;
    const previousHeight = frameHeightPrevRef.current;
    setSponsors((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        const resolved = resolveSponsorLayout(item, frameHeight, previousHeight, frameWidth);
        if (
          resolved.x !== item.x
          || resolved.y !== item.y
          || resolved.width !== item.width
          || resolved.height !== item.height
          || resolved.anchorY !== item.anchorY
          || resolved.bottom !== item.bottom
        ) {
          changed = true;
          return resolved;
        }
        return item;
      });
      return changed ? next : prev;
    });
    frameHeightPrevRef.current = frameHeight;
  }, [frameHeight, frameWidth]);

  const clearColorOverrides = () => {
    setAccent('');
    setTitleColor('');
    setTableBackground('');
    setLeagueBarBackground('');
    setStadiumBackground('');
  };

  const applyStoredStyle = (style, sourceLabel = '', { applyTitle = true } = {}) => {
    const fields = styleToFormFields(style);
    const nextTheme = fields.theme || DEFAULT_THEME;
    setTheme(nextTheme);
    setUnderGroup(underGroupFromTheme(nextTheme));
    setCustomUrl('');
    setCustomFileName('');
    setAccent(fields.accent);
    setTableBackground(fields.tableBackground);
    setLeagueBarBackground(fields.leagueBarBackground);
    setStadiumBackground(fields.stadiumBackground);
    setLogoCaption(fields.logoCaption);
    if (!isMatchCoverGoalPicture) {
      if (applyTitle) setTitle(fields.title);
      setTitleColor(fields.titleColor);
    }
    setFooterColor(fields.footerColor);
    setFooterVkEnabled(fields.footerVkEnabled);
    setFooterVkText(fields.footerVkText);
    setFooterWebEnabled(fields.footerWebEnabled);
    setFooterWebText(fields.footerWebText);
    setFooterIgEnabled(fields.footerIgEnabled);
    setFooterIgText(fields.footerIgText);
    setActiveStyleSource(sourceLabel || '');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const reloadStylesCache = async (
    leagueId,
    { applyLeagueStyle = false, pictureType: typeOverride, preserveTitle = false } = {}
  ) => {
    if (!leagueId) {
      setStylesCache(emptyStylesCache());
      return emptyStylesCache();
    }
    const type = typeOverride || pictureType;
    setStylesLoading(true);
    try {
      const next = await fetchTournamentTableStyles(dispatch, {
        leagueId,
        pictureType: type,
      });
      setStylesCache(next);
      if (applyLeagueStyle && next.leagueStyle?.style) {
        const isFallback = next.leagueStyle.match === 'fallback';
        const match = isFallback ? t('igStyleLeagueFallback') : t('igStyleLeague');
        // Never take title from fallback (another picture type) or when caller asks to keep type default
        applyStoredStyle(next.leagueStyle.style, match, {
          applyTitle: !preserveTitle && !isFallback,
        });
      }
      return next;
    } catch (error) {
      setStylesCache(emptyStylesCache());
      setMessage({ text: getApiErrorMessage(error, t('igStylesLoadError')), type: 'error' });
      return emptyStylesCache();
    } finally {
      setStylesLoading(false);
    }
  };

  const applySponsorPlacements = (list, sourceLabel = '') => {
    const next = (Array.isArray(list) ? list : []).map((item, index) => {
      const hydrated = hydrateSponsorPlacement(item, index, frameHeight);
      return frameHeight
        ? resolveSponsorLayout(hydrated, frameHeight, 0, frameWidth)
        : hydrated;
    });
    setSponsors(next);
    setSelectedSponsorId(next[0]?.id || null);
    setActiveSponsorSource(sourceLabel || '');
  };

  const reloadSponsorsCache = async (leagueId, { applyLeagueSponsors = false, pictureType: typeOverride } = {}) => {
    if (!leagueId) {
      setSponsorsCache(emptySponsorsCache());
      return emptySponsorsCache();
    }
    const type = typeOverride || pictureType;
    setSponsorsLoading(true);
    try {
      const next = await fetchTournamentTableSponsorsCache(dispatch, {
        leagueId,
        pictureType: type,
      });
      setSponsorsCache(next);
      if (applyLeagueSponsors && Array.isArray(next.leagueSponsors?.sponsors) && next.leagueSponsors.sponsors.length) {
        const label = next.leagueSponsors.match === 'fallback'
          ? t('igSponsorsLeagueFallback')
          : t('igSponsorsLeague');
        applySponsorPlacements(next.leagueSponsors.sponsors, label);
      }
      return next;
    } catch (error) {
      setSponsorsCache(emptySponsorsCache());
      setMessage({ text: getApiErrorMessage(error, t('igSponsorsLoadError')), type: 'error' });
      return emptySponsorsCache();
    } finally {
      setSponsorsLoading(false);
    }
  };

  const handleSelectPreset = (key) => {
    setTheme(key);
    setUnderGroup(underGroupFromTheme(key));
    setCustomUrl('');
    setCustomFileName('');
    clearColorOverrides();
    setActiveStyleSource('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePaletteOnly = (key) => {
    setTheme(key);
    clearColorOverrides();
    setActiveStyleSource('');
  };

  const handleRemoveUpload = () => {
    setCustomUrl('');
    setCustomFileName('');
    clearColorOverrides();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadClick = () => {
    if (!canPreview) {
      setMessage({ text: t('igUploadNeedPreview'), type: 'error' });
      return;
    }
    if (!frameHeight && !previewHtml) {
      setMessage({ text: t('igUploadNeedPreview'), type: 'error' });
      return;
    }
    setCropTarget('underlozhka');
    fileInputRef.current?.click();
  };

  const handlePreviewPhotoClick = (side) => {
    setCropTarget(side === 'right' ? 'previewRight' : 'previewLeft');
    const ref = side === 'right' ? previewRightFileRef : previewLeftFileRef;
    ref.current?.click();
  };

  const clearPreviewPhoto = (side) => {
    if (side === 'right') {
      setPreviewRightPhoto('');
      setPreviewRightFileName('');
    } else {
      setPreviewLeftPhoto('');
      setPreviewLeftFileName('');
    }
    setMessage({ text: '', type: '' });
  };

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (previewLeftFileRef.current) previewLeftFileRef.current.value = '';
    if (previewRightFileRef.current) previewRightFileRef.current.value = '';
  };

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    setUploadBusy(true);
    setMessage({ text: '', type: '' });
    try {
      const dataUrl = await readImageFileAsDataUrl(file);
      setCropSource(dataUrl);
      setCropFileName(file.name || 'custom.png');
    } catch (error) {
      const unsupported = /unsupported|type/i.test(String(error?.message || ''));
      setMessage({
        text: unsupported ? t('igUploadTypeError') : (error?.message || t('igUploadError')),
        type: 'error',
      });
      resetFileInput();
    } finally {
      setUploadBusy(false);
      resetFileInput();
    }
  };

  const handleCropCancel = () => {
    setCropSource('');
    setCropFileName('');
    setCropTarget('underlozhka');
    resetFileInput();
  };

  const handleCropComplete = async (croppedDataUrl) => {
    setUploadBusy(true);
    setMessage({ text: '', type: '' });
    try {
      const compressed = await compressDataUrl(croppedDataUrl);
      if (cropTarget === 'previewLeft') {
        setPreviewLeftPhoto(compressed);
        setPreviewLeftFileName(cropFileName || 'left.png');
        setCropSource('');
        setCropFileName('');
        setCropTarget('underlozhka');
        return;
      }
      if (cropTarget === 'previewRight') {
        setPreviewRightPhoto(compressed);
        setPreviewRightFileName(cropFileName || 'right.png');
        setCropSource('');
        setCropFileName('');
        setCropTarget('underlozhka');
        return;
      }
      setCustomUrl(compressed);
      setCustomFileName(cropFileName || 'custom.png');
      setCropSource('');
      setCropFileName('');
      clearColorOverrides();
      setActiveStyleSource('');
      setTheme((current) => {
        const solidKeys = (catalog.solid || []).map((item) => item.key);
        if (solidKeys.includes(current)) return current;
        return solidKeys[0] || 'blue';
      });
      // Keep current underlay tab — upload is available in both groups.
    } catch (error) {
      setMessage({ text: error?.message || t('igUploadError'), type: 'error' });
    } finally {
      setUploadBusy(false);
      resetFileInput();
    }
  };

  const handleCountryChange = async (countryId) => {
    const country = countryId ? findByValue(countries, countryId) : null;
    setSelectedCountry(country || null);
    setSelectedLeague(null);
    setSelectedSeason(null);
    setLeagues([]);
    setSeasons([]);
    resetScheduleFilters();
    resetGroupTableFilters();
    resetCalendarFilters();
    resetAfishaFilters();
    resetPreviewFilters();
    resetPlayoffFilters();
    resetQuoteFilters();
    resetAchievementsFilters();
    resetTourTeamFilters();
    resetMvpFilters();
    resetMatchSubsFilters();
    setPreviewHtml('');
    setSponsors([]);
    setSelectedSponsorId(null);
    setActiveSponsorSource('');
    setSponsorPickKey('');
    setMessage({ text: '', type: '' });
    setStylesCache(emptyStylesCache());
    setSponsorsCache(emptySponsorsCache());
    setActiveStyleSource('');
    await loadLeaguesForCountry(country ? country.value : null);
    resetMobileViewportAfterSelect();
  };

  const handleLeagueChange = async (leagueId) => {
    const league = leagueId ? findByValue(leagues, leagueId) : null;
    setSelectedLeague(league || null);
    setSelectedSeason(null);
    setSeasons([]);
    resetScheduleFilters();
    resetGroupTableFilters();
    resetCalendarFilters();
    resetAfishaFilters();
    resetPreviewFilters();
    resetPlayoffFilters();
    resetQuoteFilters();
    resetAchievementsFilters();
    resetTourTeamFilters();
    resetMvpFilters();
    resetMatchSubsFilters();
    setPreviewHtml('');
    setSponsors([]);
    setSelectedSponsorId(null);
    setActiveSponsorSource('');
    setSponsorPickKey('');
    setMessage({ text: '', type: '' });
    setActiveStyleSource('');
    // Title is per picture type, not per league style (fallback styles can carry another type's title)
    setTitle(defaultTitleForPictureType(pictureType));
    await loadSeasonsForLeague(league);
    if (league?.value) {
      await Promise.all([
        reloadStylesCache(league.value, { applyLeagueStyle: true, preserveTitle: true }),
        reloadSponsorsCache(league.value, { applyLeagueSponsors: true }),
      ]);
    } else {
      setStylesCache(emptyStylesCache());
      setSponsorsCache(emptySponsorsCache());
    }
    resetMobileViewportAfterSelect();
  };

  const handleSeasonChange = async (seasonId) => {
    const season = findByValue(seasons, seasonId);
    setSelectedSeason(season || null);
    setSelectedStage(null);
    setStages([]);
    resetGroupTableFilters();
    resetPlayoffFilters();
    resetMatchListPicker();
    setSelectedPreviewMatch(null);
    setPreviewMatches([]);
    setSelectedQuoteTeam(null);
    setQuoteTeams([]);
    setSelectedTourTeam(null);
    setTourTeams([]);
    resetMvpFilters();
    resetMatchSubsFilters();
    setMessage({ text: '', type: '' });
    resetMobileViewportAfterSelect();
    if (
      isMatchListPictureType(pictureType)
      && scheduleFilterMode === 'stage'
      && selectedLeague?.value
      && season?.value
    ) {
      await loadStagesForSeason(selectedLeague.value, season.value);
    }
    if (
      pictureType === PICTURE_TYPE_GROUP_TABLES
      && selectedLeague?.value
      && season?.value
    ) {
      await loadSeasonGroups(selectedLeague.value, season.value, { preselect: true });
    }
    if (
      (pictureType === PICTURE_TYPE_TOP10 || pictureType === PICTURE_TYPE_CARDS)
      && selectedLeague?.value
      && season?.value
    ) {
      await loadSeasonGroups(selectedLeague.value, season.value, { preselect: false });
    }
    if (
      isSingleMatchPictureType(pictureType)
      && selectedLeague?.value
      && season?.value
    ) {
      await loadStagesForSeason(selectedLeague.value, season.value);
    }
    if (
      (pictureType === PICTURE_TYPE_QUOTE || pictureType === PICTURE_TYPE_MVP)
      && selectedLeague?.value
      && season?.value
    ) {
      await loadQuoteTeams(selectedLeague.value, season.value);
    }
    if (
      isTourTeamPictureType(pictureType)
      && selectedLeague?.value
      && season?.value
    ) {
      await loadTourTeams(selectedLeague.value, season.value);
    }
  };

  const handleScheduleFilterModeChange = async (mode) => {
    if (mode === scheduleFilterMode) return;
    setScheduleFilterMode(mode);
    resetMatchListPicker();
    setPreviewHtml('');
    setFrameHeight(0);
    setMessage({ text: '', type: '' });
    if (mode === 'stage') {
      setScheduleDateFrom('');
      setScheduleDateTo('');
      if (selectedLeague?.value && selectedSeason?.value) {
        await loadStagesForSeason(selectedLeague.value, selectedSeason.value);
      }
    } else {
      setSelectedStage(null);
      setStages([]);
    }
  };

  const handleStageChange = async (stageId) => {
    const stage = stageId ? findByValue(stages, stageId) : null;
    setSelectedStage(stage || null);
    setSelectedPreviewMatch(null);
    setPreviewMatches([]);
    if (isMatchListPictureType(pictureType)) {
      resetMatchListPicker();
    }
    setPreviewHtml('');
    setFrameHeight(0);
    setMessage({ text: '', type: '' });
    resetMobileViewportAfterSelect();
    if (
      isSingleMatchPictureType(pictureType)
      && selectedLeague?.value
      && selectedSeason?.value
      && stage?.value
    ) {
      await loadPreviewMatches(selectedLeague.value, selectedSeason.value, stage.value);
    }
  };

  const handleScheduleDateFromChange = (value) => {
    setScheduleDateFrom(value);
    resetMatchListPicker();
    setPreviewHtml('');
    setFrameHeight(0);
    setMessage({ text: '', type: '' });
  };

  const handleScheduleDateToChange = (value) => {
    setScheduleDateTo(value);
    resetMatchListPicker();
    setPreviewHtml('');
    setFrameHeight(0);
    setMessage({ text: '', type: '' });
  };

  const toggleMatchListId = (matchId) => {
    const id = Number(matchId);
    if (!Number.isFinite(id) || id <= 0) return;
    setMatchListSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
    setPreviewHtml('');
    setFrameHeight(0);
    setMessage({ text: '', type: '' });
  };

  const selectAllMatchList = () => {
    setMatchListSelectedIds(matchListCandidates.map((item) => item.id));
    setPreviewHtml('');
    setFrameHeight(0);
    setMessage({ text: '', type: '' });
  };

  const clearMatchListSelection = () => {
    setMatchListSelectedIds([]);
    setPreviewHtml('');
    setFrameHeight(0);
    setMessage({ text: '', type: '' });
  };

  const handlePictureTypeChange = async (nextType) => {
    if (!nextType || nextType === pictureType) return;
    const nextDefault = defaultTitleForPictureType(nextType);
    setPictureType(nextType);
    resetScheduleFilters();
    resetGroupTableFilters();
    resetCalendarFilters();
    resetAfishaFilters();
    resetPreviewFilters();
    resetPlayoffFilters();
    resetQuoteFilters();
    resetAchievementsFilters();
    resetTourTeamFilters();
    resetMvpFilters();
    setPreviewHtml('');
    setFrameHeight(0);
    setFrameWidth(getCanvasWidthForPictureType(nextType));
    setSponsors([]);
    setSelectedSponsorId(null);
    setActiveSponsorSource('');
    setSponsorPickKey('');
    setActiveStyleSource('');
    setTitle(nextDefault);
    setBroadcastTournamentTitle('');
    setMessage({ text: '', type: '' });
    if (selectedLeague?.value) {
      await Promise.all([
        reloadStylesCache(selectedLeague.value, {
          applyLeagueStyle: true,
          pictureType: nextType,
          // Keep picture-type default title; league style title may be from another template
          preserveTitle: true,
        }),
        reloadSponsorsCache(selectedLeague.value, {
          applyLeagueSponsors: true,
          pictureType: nextType,
        }),
      ]);
    }
    if (
      isMatchListPictureType(nextType)
      && scheduleFilterMode === 'stage'
      && selectedLeague?.value
      && selectedSeason?.value
    ) {
      await loadStagesForSeason(selectedLeague.value, selectedSeason.value);
    }
    if (
      nextType === PICTURE_TYPE_GROUP_TABLES
      && selectedLeague?.value
      && selectedSeason?.value
    ) {
      await loadSeasonGroups(selectedLeague.value, selectedSeason.value, { preselect: true });
    }
    if (
      (nextType === PICTURE_TYPE_TOP10 || nextType === PICTURE_TYPE_CARDS)
      && selectedLeague?.value
      && selectedSeason?.value
    ) {
      await loadSeasonGroups(selectedLeague.value, selectedSeason.value, { preselect: false });
    }
    if (
      isSingleMatchPictureType(nextType)
      && selectedLeague?.value
      && selectedSeason?.value
    ) {
      await loadStagesForSeason(selectedLeague.value, selectedSeason.value);
    }
    if (
      (nextType === PICTURE_TYPE_QUOTE || nextType === PICTURE_TYPE_MVP)
      && selectedLeague?.value
      && selectedSeason?.value
    ) {
      await loadQuoteTeams(selectedLeague.value, selectedSeason.value);
    }
    if (
      isTourTeamPictureType(nextType)
      && selectedLeague?.value
      && selectedSeason?.value
    ) {
      await loadTourTeams(selectedLeague.value, selectedSeason.value);
    }
    resetMobileViewportAfterSelect();
  };

  const handleDownload = async () => {
    if (!canPreview || downloading) return;
    setDownloading(true);
    setMessage({ text: '', type: '' });
    try {
      const leagueName = selectedLeague?.name || 'league';
      const safe = (value) => String(value).replace(/[^\w\-а-яёА-ЯЁ]+/gi, '_').slice(0, 40);
      let suffix = safe(selectedSeason?.name || 'season');
      let prefix = 'table';
      if (pictureType === PICTURE_TYPE_TOP10) prefix = 'top10';
      else if (pictureType === PICTURE_TYPE_CARDS) prefix = 'cards';
      else if (pictureType === PICTURE_TYPE_DISQUALIFICATIONS) prefix = 'disqualifications';
      else if (pictureType === PICTURE_TYPE_AFISHA) {
        prefix = 'afisha';
        if (afishaDate) suffix = afishaDate;
      }
      else if (pictureType === PICTURE_TYPE_PLAYOFF) {
        prefix = playoffVerticalLayout ? 'playoff_vertical' : 'playoff';
      }
      else if (pictureType === PICTURE_TYPE_PREVIEW) {
        prefix = 'preview';
        if (selectedPreviewMatch?.value) suffix = String(selectedPreviewMatch.value);
      }
      else if (pictureType === PICTURE_TYPE_DETAILED_RESULT) {
        prefix = 'detailed_result';
        if (selectedPreviewMatch?.value) suffix = String(selectedPreviewMatch.value);
      }
      else if (pictureType === PICTURE_TYPE_MATCH_STATS) {
        prefix = 'match_stats';
        if (selectedPreviewMatch?.value) suffix = String(selectedPreviewMatch.value);
      }
      else if (pictureType === PICTURE_TYPE_MATCH_SUBS) {
        prefix = 'match_subs';
        if (selectedPreviewMatch?.value) suffix = String(selectedPreviewMatch.value);
      }
      else if (pictureType === PICTURE_TYPE_MATCH_LINEUPS) {
        prefix = 'match_lineups';
        if (selectedPreviewMatch?.value) suffix = String(selectedPreviewMatch.value);
      }
      else if (pictureType === PICTURE_TYPE_ANNOUNCEMENT) prefix = 'announcement';
      else if (pictureType === PICTURE_TYPE_QUOTE) {
        prefix = 'quote';
        if (selectedQuoteTeam?.name) suffix = safe(selectedQuoteTeam.name);
      }
      else if (pictureType === PICTURE_TYPE_ACHIEVEMENTS) prefix = 'achievements';
      else if (pictureType === PICTURE_TYPE_MVP) {
        prefix = 'mvp';
        if (mvpPlayer) {
          const family = String(mvpPlayer.family || '').trim();
          const name = String(mvpPlayer.name || '').trim();
          const label = [family, name].filter(Boolean).join('_');
          if (label) suffix = safe(label);
        }
      }
      else if (pictureType === PICTURE_TYPE_MATCH_COVER_GOAL) {
        prefix = 'plashki';
        if (selectedPreviewMatch?.value) suffix = String(selectedPreviewMatch.value);
      }
      else if (pictureType === PICTURE_TYPE_BROADCAST_PREVIEW) {
        prefix = 'broadcast_preview';
        if (selectedPreviewMatch?.value) suffix = String(selectedPreviewMatch.value);
      }
      else if (pictureType === PICTURE_TYPE_TOUR_TEAM) {
        prefix = 'tour_team';
        if (selectedTourTeam?.name) suffix = safe(selectedTourTeam.name);
      }
      else if (pictureType === PICTURE_TYPE_CHESS) prefix = 'chess';
      else if (pictureType === PICTURE_TYPE_GROUP_TABLES) prefix = 'group_tables';
      else if (pictureType === PICTURE_TYPE_CALENDAR) prefix = 'calendar';
      else if (isMatchListPictureType(pictureType)) {
        prefix = pictureType === PICTURE_TYPE_RESULTS ? 'results' : 'schedule';
        if (scheduleFilterMode === 'stage' && selectedStage?.name) {
          suffix = safe(selectedStage.name);
        } else if (scheduleDateFrom && scheduleDateTo) {
          suffix = `${scheduleDateFrom}_${scheduleDateTo}`;
        }
      }
      const filename = pictureType === PICTURE_TYPE_MATCH_COVER_GOAL
        ? `${prefix}_${safe(leagueName)}_${suffix}.zip`
        : `${prefix}_${safe(leagueName)}_${suffix}.png`;
      const sponsorsPayload = pictureType === PICTURE_TYPE_MATCH_COVER_GOAL
        ? undefined
        : buildSponsorsApiPayload(sponsors, frameHeight);
      const blob = await fetchSocialPicturePngBlob(dispatch, {
        ...formState,
        ...(sponsorsPayload ? { sponsors: sponsorsPayload } : {}),
      });
      triggerBlobDownload(blob, filename);
      setMessage({
        text: pictureType === PICTURE_TYPE_MATCH_COVER_GOAL ? t('igCoverGoalDownloadDone') : t('igDownloadDone'),
        type: 'success',
      });
      if (selectedLeague?.value) {
        await Promise.all([
          reloadStylesCache(selectedLeague.value, { applyLeagueStyle: false }),
          ...(pictureType === PICTURE_TYPE_MATCH_COVER_GOAL
            ? []
            : [reloadSponsorsCache(selectedLeague.value, { applyLeagueSponsors: false })]),
        ]);
      }
    } catch (error) {
      setMessage({ text: getSocialPictureErrorMessage(error, t, t('igDownloadError')), type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleAddSponsor = async (poolItem) => {
    if (!canPreview || !frameHeight) {
      setMessage({ text: t('igSponsorsNeedPreview'), type: 'error' });
      return;
    }
    if (!poolItem) return;
    setSponsorsBusy(true);
    try {
      const previous = (selectedSponsorId
        ? sponsors.find((item) => item.id === selectedSponsorId)
        : null) || sponsors[sponsors.length - 1] || null;
      const placement = await createSponsorPlacement({
        poolItem,
        canvasWidth: frameWidth || DEFAULT_CANVAS_WIDTH,
        canvasHeight: frameHeight,
        offsetIndex: sponsors.length,
        previous,
      });
      setSponsors((prev) => [...prev, placement]);
      setSelectedSponsorId(placement.id);
      setActiveSponsorSource('');
    } catch (error) {
      setMessage({ text: error?.message || t('igSponsorsAddError'), type: 'error' });
    } finally {
      setSponsorsBusy(false);
    }
  };

  const handleAddPickedSponsor = async () => {
    const poolItem = sponsorPool.find((item) => item.key === sponsorPickKey);
    if (!poolItem) return;
    await handleAddSponsor(poolItem);
  };

  const handleSponsorChange = (id, next) => {
    setSponsors((prev) => prev.map((item) => {
      if (item.id !== id) return item;
      return syncSponsorBottomOffset({ ...item, ...next }, frameHeight);
    }));
    setActiveSponsorSource('');
  };

  const handleRemoveSponsor = (id) => {
    setSponsors((prev) => prev.filter((item) => item.id !== id));
    setSelectedSponsorId((current) => (current === id ? null : current));
    setActiveSponsorSource('');
  };

  const handleBringSponsorFront = (id) => {
    setSponsors((prev) => {
      const item = prev.find((entry) => entry.id === id);
      if (!item) return prev;
      return [...prev.filter((entry) => entry.id !== id), item];
    });
    setSelectedSponsorId(id);
  };

  const handleSaveScheme = async () => {
    if (!selectedLeague?.value || stylesBusy) return;
    const name = schemeName.trim();
    if (!name) {
      setMessage({ text: t('igSchemeNameRequired'), type: 'error' });
      return;
    }
    setStylesBusy(true);
    setMessage({ text: '', type: '' });
    try {
      await saveTournamentTableScheme(dispatch, {
        leagueId: selectedLeague.value,
        championatId: stylesCache.championat_id,
        name,
        customBg: formState.customBg,
      });
      setSchemeName('');
      await reloadStylesCache(selectedLeague.value, { applyLeagueStyle: false });
      setMessage({ text: t('igSchemeSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('igSchemeSaveError')), type: 'error' });
    } finally {
      setStylesBusy(false);
    }
  };

  const handleSaveLeagueStyle = async () => {
    if (!selectedLeague?.value || stylesBusy) return;
    setStylesBusy(true);
    setMessage({ text: '', type: '' });
    try {
      await saveTournamentTableLeagueStyle(dispatch, {
        leagueId: selectedLeague.value,
        customBg: formState.customBg,
        pictureType,
      });
      await reloadStylesCache(selectedLeague.value, { applyLeagueStyle: false });
      setMessage({ text: t('igStyleLeagueSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('igStyleLeagueSaveError')), type: 'error' });
    } finally {
      setStylesBusy(false);
    }
  };

  const handleDeleteScheme = async (schemeId) => {
    if (!schemeId || stylesBusy) return;
    setStylesBusy(true);
    setMessage({ text: '', type: '' });
    try {
      await deleteTournamentTableScheme(dispatch, {
        id: schemeId,
        championatId: stylesCache.championat_id,
        leagueId: selectedLeague?.value,
      });
      await reloadStylesCache(selectedLeague?.value, { applyLeagueStyle: false });
      setMessage({ text: t('igSchemeDeleted'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('igSchemeDeleteError')), type: 'error' });
    } finally {
      setStylesBusy(false);
    }
  };

  const handleSaveSponsorScheme = async () => {
    if (!selectedLeague?.value || sponsorsBusy) return;
    const name = sponsorSchemeName.trim();
    if (!name) {
      setMessage({ text: t('igSponsorsSchemeNameRequired'), type: 'error' });
      return;
    }
    if (!sponsors.length) {
      setMessage({ text: t('igSponsorsEmpty'), type: 'error' });
      return;
    }
    setSponsorsBusy(true);
    setMessage({ text: '', type: '' });
    try {
      await saveTournamentTableSponsorScheme(dispatch, {
        leagueId: selectedLeague.value,
        championatId: sponsorsCache.championat_id || stylesCache.championat_id,
        name,
        sponsors: buildSponsorsApiPayload(sponsors, frameHeight),
      });
      setSponsorSchemeName('');
      await reloadSponsorsCache(selectedLeague.value, { applyLeagueSponsors: false });
      setMessage({ text: t('igSponsorsSchemeSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('igSponsorsSchemeSaveError')), type: 'error' });
    } finally {
      setSponsorsBusy(false);
    }
  };

  const handleDeleteSponsorScheme = async (schemeId) => {
    if (!schemeId || sponsorsBusy) return;
    setSponsorsBusy(true);
    setMessage({ text: '', type: '' });
    try {
      await deleteTournamentTableSponsorScheme(dispatch, {
        id: schemeId,
        championatId: sponsorsCache.championat_id || stylesCache.championat_id,
        leagueId: selectedLeague?.value,
      });
      await reloadSponsorsCache(selectedLeague?.value, { applyLeagueSponsors: false });
      setMessage({ text: t('igSponsorsSchemeDeleted'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('igSponsorsSchemeDeleteError')), type: 'error' });
    } finally {
      setSponsorsBusy(false);
    }
  };

  return (
    <div className="layoutWrapper">
      {cropSource ? (
        <ImageCropper
          image={cropSource}
          aspect={cropAspect}
          title={
            cropTarget === 'previewLeft' || cropTarget === 'previewRight'
              ? t('igPreviewPhotoCrop')
              : t('igCropTitle')
          }
          outputWidth={cropOutputWidth}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      ) : null}
      <div className="matchCenterWrapper ig-page">
        <div className="matchCenterHeader">
          <div className="ig-header">
            <div className="ig-header-text">
              <span className="matchCenterName">{t('navInfographics')}</span>
            </div>
            <button
              type="button"
              className="ig-download-btn"
              onClick={handleDownload}
              disabled={!canPreview || downloading || previewLoading || uploadBusy}
            >
              {downloading ? <Loader type="TailSpin" color="#101510" height={18} width={18} /> : <FiDownload />}
              {downloading
                ? t('igDownloading')
                : (isMatchCoverGoalPicture ? t('igCoverGoalDownload') : t('igDownload'))}
            </button>
          </div>

          <div className="ig-filters">
            <div className="ig-filter-group">
              <label>{t('igPictureType')}</label>
              <SelectSearch
                options={pictureTypeOptions}
                search
                value={pictureType}
                onChange={handlePictureTypeChange}
                filterOptions={filterPictureTypeOptions}
                emptyMessage={t('igPictureTypeNotFound')}
                placeholder={t('igPictureTypePh')}
              />
            </div>

            <div className="ig-filter-group">
              <label>{t('gmCountry')}</label>
              <SelectSearch
                options={countries}
                search
                disabled={!champId}
                value={selectedCountry?.value}
                onChange={handleCountryChange}
                filterOptions={fuzzySearch}
                emptyMessage={t('cmCountryNotFound')}
                placeholder={t('cmCountryPh')}
              />
            </div>

            <div className="ig-filter-group">
              <label>{t('league')}</label>
              <SelectSearch
                options={leagues}
                search
                disabled={!selectedCountry}
                value={selectedLeague?.value}
                onChange={handleLeagueChange}
                filterOptions={fuzzySearch}
                emptyMessage={t('leagueNotFound')}
                placeholder={t('leaguePlaceholder')}
              />
            </div>

            {!isMatchListPicture ? (
              <div className="ig-filter-group">
                <label>{t('stSeason')}</label>
                <SelectSearch
                  options={seasons}
                  search
                  disabled={!selectedLeague}
                  value={selectedSeason?.value}
                  onChange={handleSeasonChange}
                  filterOptions={fuzzySearch}
                  emptyMessage={t('stSeasonNotFound')}
                  placeholder={t('stSeasonPlaceholder')}
                />
              </div>
            ) : null}

            {isAfishaPicture ? (
              <>
                <div className="ig-filter-group ig-schedule-dates-group">
                  <label htmlFor="ig-afisha-date">{t('igAfishaDate')}</label>
                  <input
                    id="ig-afisha-date"
                    type="date"
                    className="ig-schedule-date-input"
                    value={afishaDate}
                    disabled={!selectedSeason}
                    onChange={(event) => {
                      setAfishaDate(event.target.value);
                      setPreviewHtml('');
                      setFrameHeight(0);
                      setMessage({ text: '', type: '' });
                    }}
                  />
                </div>
                <div className="ig-filter-group">
                  <label htmlFor="ig-afisha-code">{t('igAfishaCode')}</label>
                  <input
                    id="ig-afisha-code"
                    className="ig-input"
                    type="text"
                    value={afishaCode}
                    disabled={!selectedSeason}
                    onChange={(event) => {
                      setAfishaCode(event.target.value);
                      setPreviewHtml('');
                      setFrameHeight(0);
                      setMessage({ text: '', type: '' });
                    }}
                    placeholder={t('igAfishaCodePh')}
                    maxLength={64}
                  />
                  <p className="ig-sponsor-hint" style={{ margin: '6px 0 0' }}>{t('igAfishaCodeHint')}</p>
                </div>
              </>
            ) : null}

            {isPlayoffPicture ? (
              <>
                {playoffAvailableTracks.length >= 2 ? (
                  <div className={`ig-filter-group ig-schedule-mode-group ig-groups-picker-group${!selectedSeason ? ' is-disabled' : ''}`}>
                    <label>{t('igPlayoffTracks')}</label>
                    {selectedSeason ? (
                      <div className="ig-schedule-mode" role="group" aria-label={t('igPlayoffTracks')}>
                        {playoffAvailableTracks.map((track) => {
                          const active = playoffSelectedTrackKeys.includes(track.key);
                          return (
                            <button
                              key={`po-track-${track.key}`}
                              type="button"
                              className={`ig-type-chip${active ? ' is-active' : ''}`}
                              onClick={() => {
                                // Empty selection = all cups (field omitted). First click from empty
                                // starts a subset; clearing last chip returns to all.
                                const next = playoffSelectedTrackKeys.length === 0
                                  ? [track.key]
                                  : (playoffSelectedTrackKeys.includes(track.key)
                                    ? playoffSelectedTrackKeys.filter((key) => key !== track.key)
                                    : [...playoffSelectedTrackKeys, track.key]);
                                const allKeys = playoffAvailableTracks.map((item) => item.key);
                                const selectsAll = !next.length
                                  || (next.length === allKeys.length
                                    && allKeys.every((key) => next.includes(key)));
                                const nextKeys = selectsAll ? [] : next;
                                setPlayoffSelectedTrackKeys(nextKeys);
                                if (nextKeys.length) {
                                  setPlayoffSelectedStageIds((prev) => prev.filter((id) => {
                                    const stage = playoffAvailableStages.find((item) => item.stage_id === id);
                                    return !stage?.track_key || nextKeys.includes(stage.track_key);
                                  }));
                                }
                                setSponsors([]);
                                setSelectedSponsorId(null);
                                setActiveSponsorSource('');
                                setPreviewHtml('');
                                setFrameHeight(0);
                                setMessage({ text: '', type: '' });
                              }}
                            >
                              {track.title}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="ig-filter-slot-placeholder" aria-hidden={!selectedSeason}>
                        <p className="ig-sponsor-hint" style={{ margin: 0 }}>{t('igFiltersNeedSeason')}</p>
                      </div>
                    )}
                    <p className="ig-sponsor-hint" style={{ margin: 0 }}>{t('igPlayoffTracksHint')}</p>
                    {playoffSelectedTrackKeys.length ? (
                      <button
                        type="button"
                        className="ig-clear-btn"
                        style={{ marginTop: 6 }}
                        onClick={() => {
                          setPlayoffSelectedTrackKeys([]);
                          setSponsors([]);
                          setSelectedSponsorId(null);
                          setActiveSponsorSource('');
                          setPreviewHtml('');
                          setFrameHeight(0);
                          setMessage({ text: '', type: '' });
                        }}
                      >
                        {t('igPlayoffTracksClear')}
                      </button>
                    ) : null}
                  </div>
                ) : null}

                <div className={`ig-filter-group ig-schedule-mode-group ig-groups-picker-group${!selectedSeason ? ' is-disabled' : ''}`}>
                  <label>{t('igPlayoffStages')}</label>
                  {selectedSeason && playoffAvailableStages.length ? (
                    <div className="ig-schedule-mode" role="group" aria-label={t('igPlayoffStages')}>
                      {(playoffSelectedTrackKeys.length
                        ? playoffAvailableStages.filter((stage) => (
                          !stage.track_key || playoffSelectedTrackKeys.includes(stage.track_key)
                        ))
                        : playoffAvailableStages
                      ).map((stage) => {
                        const active = playoffSelectedStageIds.includes(stage.stage_id);
                        const stageLabel = playoffAvailableTracks.length >= 2 && stage.track_title
                          ? `${stage.track_title}: ${stage.title}`
                          : stage.title;
                        return (
                          <button
                            key={`po-stage-${stage.stage_id}`}
                            type="button"
                            className={`ig-type-chip${active ? ' is-active' : ''}`}
                            onClick={() => {
                              const next = playoffSelectedStageIds.includes(stage.stage_id)
                                ? playoffSelectedStageIds.filter((id) => id !== stage.stage_id)
                                : [...playoffSelectedStageIds, stage.stage_id];
                              if (!isPlayoffStageSelectionValid(next, playoffAvailableStages)) {
                                setMessage({ text: t('igPlayoffStagesInvalid'), type: 'error' });
                                return;
                              }
                              setPlayoffSelectedStageIds(next);
                              setSponsors([]);
                              setSelectedSponsorId(null);
                              setActiveSponsorSource('');
                              setPreviewHtml('');
                              setFrameHeight(0);
                              setMessage({ text: '', type: '' });
                            }}
                          >
                            {stageLabel}
                            {Number(stage.matches) === 0 ? ` · ${t('igPlayoffStageEmpty')}` : ''}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="ig-filter-slot-placeholder" aria-hidden={!selectedSeason}>
                      <p className="ig-sponsor-hint" style={{ margin: 0 }}>
                        {!selectedSeason ? t('igFiltersNeedSeason') : t('igPlayoffStagesLoading')}
                      </p>
                    </div>
                  )}
                  <p className="ig-sponsor-hint" style={{ margin: 0 }}>{t('igPlayoffStagesHint')}</p>
                  {playoffSelectedStageIds.length ? (
                    <button
                      type="button"
                      className="ig-clear-btn"
                      style={{ marginTop: 6 }}
                      onClick={() => {
                        setPlayoffSelectedStageIds([]);
                        setSponsors([]);
                        setSelectedSponsorId(null);
                        setActiveSponsorSource('');
                        setPreviewHtml('');
                        setFrameHeight(0);
                        setMessage({ text: '', type: '' });
                      }}
                    >
                      {t('igPlayoffStagesClear')}
                    </button>
                  ) : null}
                </div>

                <div className={`ig-filter-group ig-schedule-mode-group ig-groups-picker-group${!selectedSeason ? ' is-disabled' : ''}`}>
                  <label>{t('igPlayoffTeams')}</label>
                  {selectedSeason && playoffAvailableTeams.length ? (
                    <div className="ig-schedule-mode" role="group" aria-label={t('igPlayoffTeams')}>
                      {playoffAvailableTeams.map((team) => {
                        const active = playoffSelectedTeamIds.includes(team.id);
                        return (
                          <button
                            key={`po-team-${team.id}`}
                            type="button"
                            className={`ig-type-chip${active ? ' is-active' : ''}`}
                            onClick={() => {
                              setPlayoffSelectedTeamIds((prev) => (
                                prev.includes(team.id)
                                  ? prev.filter((id) => id !== team.id)
                                  : [...prev, team.id]
                              ));
                              setSponsors([]);
                              setSelectedSponsorId(null);
                              setActiveSponsorSource('');
                              setPreviewHtml('');
                              setFrameHeight(0);
                              setMessage({ text: '', type: '' });
                            }}
                          >
                            {team.short_name || team.name}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="ig-filter-slot-placeholder" aria-hidden={!selectedSeason}>
                      <p className="ig-sponsor-hint" style={{ margin: 0 }}>
                        {!selectedSeason ? t('igFiltersNeedSeason') : t('igPlayoffTeamsLoading')}
                      </p>
                    </div>
                  )}
                  <p className="ig-sponsor-hint" style={{ margin: 0 }}>{t('igPlayoffTeamsHint')}</p>
                  {playoffSelectedTeamIds.length ? (
                    <button
                      type="button"
                      className="ig-clear-btn"
                      style={{ marginTop: 6 }}
                      onClick={() => {
                        setPlayoffSelectedTeamIds([]);
                        setSponsors([]);
                        setSelectedSponsorId(null);
                        setActiveSponsorSource('');
                        setPreviewHtml('');
                        setFrameHeight(0);
                        setMessage({ text: '', type: '' });
                      }}
                    >
                      {t('igPlayoffTeamsClear')}
                    </button>
                  ) : null}
                </div>

                <div className="ig-filter-group ig-schedule-mode-group">
                  <label>{t('igPlayoffLayout')}</label>
                  <label className="ig-check-row" htmlFor="ig-playoff-vertical">
                    <input
                      id="ig-playoff-vertical"
                      type="checkbox"
                      checked={playoffVerticalLayout || playoffMirrorLocked}
                      disabled={!selectedSeason || playoffMirrorLocked}
                      onChange={(event) => {
                        setPlayoffVerticalLayout(event.target.checked);
                        setSponsors([]);
                        setSelectedSponsorId(null);
                        setActiveSponsorSource('');
                        setPreviewHtml('');
                        setFrameHeight(0);
                        setFrameWidth(getCanvasWidthForPictureType(PICTURE_TYPE_PLAYOFF));
                        setMessage({ text: '', type: '' });
                      }}
                    />
                    <span>
                      {playoffMirrorLocked
                        ? t('igPlayoffVerticalForcedHint')
                        : t('igPlayoffVerticalHint')}
                    </span>
                  </label>
                </div>
              </>
            ) : null}

            {isSingleMatchPicture && canUseMyStaffMatches ? (
              <div className="ig-filter-group ig-schedule-mode-group">
                <label>{t('igMatchListMyStaff')}</label>
                <label className="ig-check-row" htmlFor="ig-preview-match-only-mine">
                  <input
                    id="ig-preview-match-only-mine"
                    type="checkbox"
                    checked={matchListOnlyMine}
                    onChange={async (event) => {
                      const next = event.target.checked;
                      setMatchListOnlyMine(next);
                      setSelectedPreviewMatch(null);
                      setPreviewHtml('');
                      setFrameHeight(0);
                      setMessage({ text: '', type: '' });
                      if (
                        selectedLeague?.value
                        && selectedSeason?.value
                        && selectedStage?.value
                      ) {
                        await loadPreviewMatches(
                          selectedLeague.value,
                          selectedSeason.value,
                          selectedStage.value,
                          { onlyMine: next }
                        );
                      } else {
                        setPreviewMatches([]);
                      }
                    }}
                  />
                  <span>{t('igMatchListMyStaffHint')}</span>
                </label>
              </div>
            ) : null}

            {isSingleMatchPicture ? (
              <div className="ig-filter-group">
                <label>{t('igScheduleStage')}</label>
                <SelectSearch
                  options={stages}
                  search
                  disabled={!selectedSeason}
                  value={selectedStage?.value}
                  onChange={handleStageChange}
                  filterOptions={fuzzySearch}
                  emptyMessage={t('igScheduleStageNotFound')}
                  placeholder={t('igScheduleStagePh')}
                />
              </div>
            ) : null}

            {isSingleMatchPicture ? (
              <div className="ig-filter-group ig-preview-match-group">
                <label>{t('igPreviewMatch')}</label>
                <SelectSearch
                  options={previewMatches}
                  search
                  disabled={!selectedStage}
                  value={selectedPreviewMatch?.value}
                  onChange={(matchId) => {
                    const match = matchId ? findByValue(previewMatches, matchId) : null;
                    setSelectedPreviewMatch(match || null);
                    resetMatchStatsBarColors();
                    resetMatchSubsFilters();
                    setPreviewHtml('');
                    setFrameHeight(0);
                    setMessage({ text: '', type: '' });
                    resetMobileViewportAfterSelect();
                  }}
                  filterOptions={fuzzySearch}
                  emptyMessage={
                    matchListOnlyMine && canUseMyStaffMatches
                      ? t('igMatchListMyStaffEmpty')
                      : t('igPreviewMatchNotFound')
                  }
                  placeholder={t('igPreviewMatchPh')}
                />
              </div>
            ) : null}

            {isMatchRosterPicture ? (
              <div className={`ig-filter-group ig-groups-picker-group${!selectedPreviewMatch ? ' is-disabled' : ''}`}>
                <label>{t('igMatchSubsRoster')}</label>
                <div className="ig-schedule-mode" role="group" aria-label={t('igMatchSubsRoster')}>
                  <button
                    type="button"
                    className={`ig-type-chip${!matchSubsManual ? ' is-active' : ''}`}
                    disabled={!selectedPreviewMatch}
                    onClick={() => enableMatchSubsManual(false)}
                  >
                    {t('igMatchSubsFromDb')}
                  </button>
                  <button
                    type="button"
                    className={`ig-type-chip${matchSubsManual ? ' is-active' : ''}`}
                    disabled={!selectedPreviewMatch}
                    onClick={() => enableMatchSubsManual(true)}
                  >
                    {t('igMatchSubsManual')}
                  </button>
                </div>
                <p className="ig-sponsor-hint" style={{ margin: '6px 0 0' }}>
                  {!selectedPreviewMatch
                    ? t('igMatchSubsNeedMatch')
                    : (matchSubsManual
                      ? (isMatchLineupsPicture ? t('igMatchLineupsManualHint') : t('igMatchSubsManualHint'))
                      : (isMatchLineupsPicture ? t('igMatchLineupsDbHint') : t('igMatchSubsDbHint')))}
                </p>
                {selectedPreviewMatch && !matchSubsManual && matchSubsDbRoster ? (
                  <button
                    type="button"
                    className={`ig-clear-btn${matchSubsEditPositions ? ' is-active' : ''}`}
                    style={{ marginTop: 8 }}
                    onClick={() => enableMatchSubsPositionEditor(!matchSubsEditPositions)}
                  >
                    {matchSubsEditPositions
                      ? t('igMatchSubsHidePositions')
                      : t('igMatchSubsEditPositions')}
                  </button>
                ) : null}
                {selectedPreviewMatch && (matchSubsManual || matchSubsEditPositions) ? (
                  <div className={`ig-match-subs-editor${matchSubsManual ? '' : ' is-db-positions'}`}>
                    <label className="ig-match-subs-overwrite">
                      <input
                        type="checkbox"
                        checked={matchSubsOverwritePositions}
                        onChange={(event) => {
                          setMatchSubsOverwritePositions(event.target.checked);
                          setPreviewHtml('');
                          setFrameHeight(0);
                          setMessage({ text: '', type: '' });
                        }}
                      />
                      <span>{t('igMatchSubsOverwritePositions')}</span>
                    </label>
                    {[
                      { block: 'starters', title: t('igMatchSubsStarters') },
                      { block: 'bench', title: t('igMatchSubsBench') },
                    ].map(({ block, title: blockTitle }) => (
                      <div key={block} className="ig-match-subs-block">
                        <div className="ig-under-section-title">{blockTitle}</div>
                        <div className="ig-match-subs-sides">
                          {['home', 'guest'].map((side) => {
                            const rows = matchSubsRoster[block]?.[side] || [];
                            const sideLabel = side === 'home'
                              ? (matchSubsTeamNames.home || t('igMatchSubsHome'))
                              : (matchSubsTeamNames.guest || t('igMatchSubsGuest'));
                            return (
                              <div key={`${block}-${side}`} className="ig-match-subs-side">
                                <div className="ig-match-subs-side-title">{sideLabel}</div>
                                <div className="ig-match-subs-rows">
                                  {rows.map((row) => {
                                    const showSlot = isMatchLineupsPicture && block === 'starters';
                                    return (
                                    <div
                                      key={row.key}
                                      className={`ig-match-subs-row${showSlot ? ' has-slot' : ''}`}
                                    >
                                      {matchSubsManual ? (
                                        <AdminPlayerPicker
                                          variant="icon"
                                          champId={champId}
                                          leagueId={selectedLeague?.value}
                                          disabled={!selectedSeason}
                                          title={t('igMatchSubsSearchPlayer')}
                                          onChange={(player) => applyMatchSubsPlayerPick(
                                            block,
                                            side,
                                            row.key,
                                            player
                                          )}
                                        />
                                      ) : null}
                                      <input
                                        className="ig-input ig-match-subs-num"
                                        type="text"
                                        inputMode="numeric"
                                        value={row.player_number}
                                        readOnly={!matchSubsManual}
                                        onChange={(event) => updateMatchSubsPlayer(
                                          block,
                                          side,
                                          row.key,
                                          'player_number',
                                          event.target.value
                                        )}
                                        placeholder="№"
                                        maxLength={3}
                                      />
                                      <input
                                        className="ig-input"
                                        type="text"
                                        value={row.family}
                                        readOnly={!matchSubsManual}
                                        onChange={(event) => updateMatchSubsPlayer(
                                          block,
                                          side,
                                          row.key,
                                          'family',
                                          event.target.value
                                        )}
                                        placeholder={t('igMatchSubsFamilyPh')}
                                        maxLength={40}
                                      />
                                      <input
                                        className="ig-input"
                                        type="text"
                                        value={row.name}
                                        readOnly={!matchSubsManual}
                                        onChange={(event) => updateMatchSubsPlayer(
                                          block,
                                          side,
                                          row.key,
                                          'name',
                                          event.target.value
                                        )}
                                        placeholder={t('igMatchSubsNamePh')}
                                        maxLength={40}
                                      />
                                      {showSlot ? (
                                        <>
                                          <input
                                            className="ig-input ig-match-subs-slot"
                                            type="text"
                                            list={`ig-ms-slot-${row.key}`}
                                            value={row.lineup_position_id || ''}
                                            readOnly={!matchSubsManual}
                                            onChange={(event) => {
                                              updateMatchSubsPlayer(
                                                block,
                                                side,
                                                row.key,
                                                'lineup_position_id',
                                                event.target.value
                                              );
                                              setPreviewHtml('');
                                              setFrameHeight(0);
                                              setMessage({ text: '', type: '' });
                                            }}
                                            placeholder={t('igMatchLineupsSlotPh')}
                                            title={t('igMatchLineupsSlotHint')}
                                            maxLength={12}
                                          />
                                          <datalist id={`ig-ms-slot-${row.key}`}>
                                            {MATCH_LINEUP_SLOT_OPTIONS.map((opt) => (
                                              <option
                                                key={opt.value}
                                                value={opt.value}
                                                label={opt.name}
                                              />
                                            ))}
                                          </datalist>
                                        </>
                                      ) : null}
                                      <input
                                        className="ig-input ig-match-subs-pos"
                                        type="text"
                                        list={`ig-ms-pos-${row.key}`}
                                        value={row.position || ''}
                                        onChange={(event) => {
                                          updateMatchSubsPlayer(
                                            block,
                                            side,
                                            row.key,
                                            'position',
                                            event.target.value
                                          );
                                          setPreviewHtml('');
                                          setFrameHeight(0);
                                          setMessage({ text: '', type: '' });
                                        }}
                                        placeholder={row.db_position || t('igMatchSubsPositionPh')}
                                        title={t('igMatchSubsPositionHint')}
                                        maxLength={8}
                                      />
                                      <datalist id={`ig-ms-pos-${row.key}`}>
                                        {MATCH_SUBS_POSITION_OPTIONS.map((opt) => (
                                          <option
                                            key={opt.value}
                                            value={opt.value}
                                            label={opt.name}
                                          />
                                        ))}
                                      </datalist>
                                      {matchSubsManual ? (
                                        <button
                                          type="button"
                                          className="ig-clear-btn"
                                          onClick={() => removeMatchSubsPlayer(block, side, row.key)}
                                          title={t('igMatchSubsRemove')}
                                        >
                                          <FiX />
                                        </button>
                                      ) : null}
                                    </div>
                                    );
                                  })}
                                </div>
                                {matchSubsManual ? (
                                  <button
                                    type="button"
                                    className="ig-clear-btn"
                                    onClick={() => addMatchSubsPlayer(block, side)}
                                  >
                                    {t('igMatchSubsAdd')}
                                  </button>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {matchSubsDbRoster && matchSubsManual ? (
                      <button
                        type="button"
                        className="ig-clear-btn"
                        onClick={() => {
                          setMatchSubsRoster(seedMatchSubsRoster(matchSubsDbRoster));
                          setPreviewHtml('');
                          setFrameHeight(0);
                          setMessage({ text: '', type: '' });
                        }}
                      >
                        {t('igMatchSubsReloadDb')}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            {isQuotePicture ? (
              <div className="ig-filter-group">
                <label>{t('igQuoteTeam')}</label>
                <SelectSearch
                  options={quoteTeams}
                  search
                  disabled={!selectedSeason}
                  value={selectedQuoteTeam?.value}
                  onChange={(teamId) => {
                    const team = teamId ? findByValue(quoteTeams, teamId) : null;
                    setSelectedQuoteTeam(team || null);
                    setPreviewHtml('');
                    setFrameHeight(0);
                    setMessage({ text: '', type: '' });
                    resetMobileViewportAfterSelect();
                  }}
                  filterOptions={fuzzySearch}
                  emptyMessage={t('igQuoteTeamNotFound')}
                  placeholder={t('igQuoteTeamPh')}
                />
              </div>
            ) : null}

            {isMvpPicture ? (
              <>
                <div className="ig-filter-group">
                  <label>{t('igMvpPlayer')}</label>
                  <AdminPlayerPicker
                    champId={champId}
                    leagueId={selectedLeague?.value}
                    value={mvpPlayer}
                    disabled={!selectedSeason}
                    onChange={(player) => {
                      applyMvpPlayer(player);
                      setPreviewHtml('');
                      setFrameHeight(0);
                      setMessage({ text: '', type: '' });
                      resetMobileViewportAfterSelect();
                    }}
                  />
                </div>
                <div className="ig-filter-group">
                  <label>{t('igMvpTeam')}</label>
                  <SelectSearch
                    options={quoteTeams}
                    search
                    disabled={!selectedSeason}
                    value={selectedMvpTeam?.value}
                    onChange={(teamId) => {
                      const team = teamId ? findByValue(quoteTeams, teamId) : null;
                      setSelectedMvpTeam(team || null);
                      setPreviewHtml('');
                      setFrameHeight(0);
                      setMessage({ text: '', type: '' });
                      resetMobileViewportAfterSelect();
                    }}
                    filterOptions={fuzzySearch}
                    emptyMessage={t('igQuoteTeamNotFound')}
                    placeholder={t('igMvpTeamPh')}
                  />
                </div>
              </>
            ) : null}

            {isTourTeamPicture ? (
              <div className="ig-filter-group">
                <label className="ig-filter-label-row">
                  <span>{t('igTourTeamPick')}</span>
                  <button
                    type="button"
                    className="ig-filter-inline-link"
                    onClick={openTourTeamEditor}
                    title={t('igTourTeamHint')}
                    disabled={!selectedLeague}
                  >
                    <FiExternalLink /> {t('igTourTeamOpenEditor')}
                  </button>
                </label>
                <SelectSearch
                  options={tourTeams}
                  search
                  disabled={!selectedSeason}
                  value={selectedTourTeam?.value}
                  onChange={(tourTeamId) => {
                    const team = tourTeamId ? findByValue(tourTeams, tourTeamId) : null;
                    setSelectedTourTeam(team || null);
                    setPreviewHtml('');
                    setFrameHeight(0);
                    setMessage({ text: '', type: '' });
                    resetMobileViewportAfterSelect();
                  }}
                  filterOptions={fuzzySearch}
                  emptyMessage={t('igTourTeamNotFound')}
                  placeholder={t('igTourTeamPh')}
                />
              </div>
            ) : null}

            {isTopStatsPicture ? (
              <>
                <div className={`ig-filter-group ig-schedule-mode-group${!selectedSeason ? ' is-disabled' : ''}`}>
                  <label>{t('igSplitByGroups')}</label>
                  <div className="ig-schedule-mode" role="tablist" aria-label={t('igSplitByGroups')}>
                    <button
                      type="button"
                      className={`ig-type-chip${splitByGroups ? ' is-active' : ''}`}
                      disabled={!selectedSeason}
                      onClick={() => {
                        if (splitByGroups) return;
                        setSplitByGroups(true);
                        setPreviewHtml('');
                        setFrameHeight(0);
                        setMessage({ text: '', type: '' });
                      }}
                    >
                      {t('igSplitByGroupsOn')}
                    </button>
                    <button
                      type="button"
                      className={`ig-type-chip${!splitByGroups ? ' is-active' : ''}`}
                      disabled={!selectedSeason}
                      onClick={() => {
                        if (!splitByGroups) return;
                        setSplitByGroups(false);
                        setPreviewHtml('');
                        setFrameHeight(0);
                        setMessage({ text: '', type: '' });
                      }}
                    >
                      {t('igSplitByGroupsOff')}
                    </button>
                  </div>
                </div>
                {splitByGroups ? (
                  <div className={`ig-filter-group ig-schedule-mode-group ig-groups-picker-group${!selectedSeason ? ' is-disabled' : ''}`}>
                    <label>{t('igTopStatsGroupsPick')}</label>
                    {selectedSeason && seasonGroups.length ? (
                      <div className="ig-schedule-mode" role="group" aria-label={t('igTopStatsGroupsPick')}>
                        {seasonGroups.map((group) => {
                          const active = selectedGroupNames.includes(group.name);
                          return (
                            <button
                              key={`top-${group.id}-${group.name}`}
                              type="button"
                              className={`ig-type-chip${active ? ' is-active' : ''}`}
                              onClick={() => {
                                setSelectedGroupNames((prev) => (
                                  prev.includes(group.name)
                                    ? prev.filter((name) => name !== group.name)
                                    : [...prev, group.name]
                                ));
                                setPreviewHtml('');
                                setFrameHeight(0);
                                setMessage({ text: '', type: '' });
                              }}
                            >
                              {group.name}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="ig-filter-slot-placeholder" aria-hidden="true">
                        <p className="ig-sponsor-hint" style={{ margin: 0 }}>
                          {selectedSeason ? t('igGroupTablesEmpty') : t('igFiltersNeedSeason')}
                        </p>
                      </div>
                    )}
                    <p className="ig-sponsor-hint" style={{ margin: 0 }}>{t('igTopStatsGroupsHint')}</p>
                  </div>
                ) : null}
              </>
            ) : null}

            {isGroupTablesPicture ? (
              <div className={`ig-filter-group ig-schedule-mode-group ig-groups-picker-group${!selectedSeason ? ' is-disabled' : ''}`}>
                <label>{t('igGroupTablesPick')}</label>
                {selectedSeason && seasonGroups.length ? (
                  <div className="ig-schedule-mode" role="group" aria-label={t('igGroupTablesPick')}>
                    {seasonGroups.map((group) => {
                      const active = selectedGroupNames.includes(group.name);
                      return (
                        <button
                          key={`${group.id}-${group.name}`}
                          type="button"
                          className={`ig-type-chip${active ? ' is-active' : ''}`}
                          onClick={() => {
                            setSelectedGroupNames((prev) => {
                              if (prev.includes(group.name)) {
                                return prev.filter((name) => name !== group.name);
                              }
                              if (prev.length >= 4) return prev;
                              return [...prev, group.name];
                            });
                            setPreviewHtml('');
                            setFrameHeight(0);
                            setMessage({ text: '', type: '' });
                          }}
                        >
                          {group.name}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="ig-filter-slot-placeholder" aria-hidden="true">
                    <p className="ig-sponsor-hint" style={{ margin: 0 }}>
                      {selectedSeason ? t('igGroupTablesEmpty') : t('igFiltersNeedSeason')}
                    </p>
                  </div>
                )}
                <p className="ig-sponsor-hint" style={{ margin: 0 }}>{t('igGroupTablesPickHint')}</p>
              </div>
            ) : null}

            {selectedLeague && isCalendarPicture && selectedSeason ? (
              <>
                <div className="ig-filter-group ig-schedule-mode-group">
                  <label>{t('igCalendarColumns')}</label>
                  <div className="ig-schedule-mode" role="group" aria-label={t('igCalendarColumns')}>
                    {[1, 2, 3, 4].map((count) => (
                      <button
                        key={`cols-${count}`}
                        type="button"
                        className={`ig-type-chip${calendarColumns === count ? ' is-active' : ''}`}
                        onClick={() => {
                          setCalendarColumns(count);
                          setPreviewHtml('');
                          setFrameHeight(0);
                          setMessage({ text: '', type: '' });
                        }}
                      >
                        {count}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ig-filter-group ig-schedule-mode-group">
                  <label>{t('igCalendarGroupStagesOnly')}</label>
                  <label className="ig-check-row" htmlFor="ig-group-stages-only">
                    <input
                      id="ig-group-stages-only"
                      type="checkbox"
                      checked={groupStagesOnly}
                      onChange={(event) => {
                        setGroupStagesOnly(event.target.checked);
                        setPreviewHtml('');
                        setFrameHeight(0);
                        setMessage({ text: '', type: '' });
                      }}
                    />
                    <span>{t('igCalendarGroupStagesOnlyHint')}</span>
                  </label>
                </div>
              </>
            ) : null}

            {selectedLeague && isMatchListPicture ? (
              <>
                <div className="ig-filter-group ig-schedule-mode-group">
                  <label>{t('igScheduleFilter')}</label>
                  <div className="ig-schedule-mode" role="tablist" aria-label={t('igScheduleFilter')}>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={scheduleFilterMode === 'stage'}
                      className={`ig-type-chip${scheduleFilterMode === 'stage' ? ' is-active' : ''}`}
                      onClick={() => handleScheduleFilterModeChange('stage')}
                    >
                      {t('igScheduleByStage')}
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={scheduleFilterMode === 'dates'}
                      className={`ig-type-chip${scheduleFilterMode === 'dates' ? ' is-active' : ''}`}
                      onClick={() => handleScheduleFilterModeChange('dates')}
                    >
                      {t('igScheduleByDates')}
                    </button>
                  </div>
                </div>

                {scheduleFilterMode === 'stage' ? (
                  <div className="ig-filter-group">
                    <label>{t('stSeason')}</label>
                    <SelectSearch
                      options={seasons}
                      search
                      value={selectedSeason?.value}
                      onChange={handleSeasonChange}
                      filterOptions={fuzzySearch}
                      emptyMessage={t('stSeasonNotFound')}
                      placeholder={t('stSeasonPlaceholder')}
                    />
                  </div>
                ) : null}

                {scheduleFilterMode === 'stage' && selectedSeason ? (
                  <div className="ig-filter-group">
                    <label>{t('igScheduleStage')}</label>
                    <SelectSearch
                      options={stages}
                      search
                      value={selectedStage?.value}
                      onChange={handleStageChange}
                      filterOptions={fuzzySearch}
                      emptyMessage={t('igScheduleStageNotFound')}
                      placeholder={t('igScheduleStagePh')}
                    />
                  </div>
                ) : null}

                {scheduleFilterMode === 'dates' ? (
                  <>
                    <div className="ig-filter-group ig-schedule-dates-group">
                      <label>{t('igScheduleByDates')}</label>
                      <div className="ig-schedule-dates-row">
                        <input
                          id="ig-schedule-date-from"
                          type="date"
                          className="ig-schedule-date-input"
                          aria-label={t('igScheduleDateFrom')}
                          value={scheduleDateFrom}
                          onChange={(event) => handleScheduleDateFromChange(event.target.value)}
                        />
                        <span className="ig-schedule-dates-sep" aria-hidden="true">–</span>
                        <input
                          id="ig-schedule-date-to"
                          type="date"
                          className="ig-schedule-date-input"
                          aria-label={t('igScheduleDateTo')}
                          value={scheduleDateTo}
                          onChange={(event) => handleScheduleDateToChange(event.target.value)}
                        />
                      </div>
                    </div>
                    <div className="ig-filter-group">
                      <label>{t('igScheduleSeasonOptional')}</label>
                      <SelectSearch
                        options={seasons}
                        search
                        value={selectedSeason?.value}
                        onChange={handleSeasonChange}
                        filterOptions={fuzzySearch}
                        emptyMessage={t('stSeasonNotFound')}
                        placeholder={t('igScheduleSeasonOptionalPh')}
                      />
                    </div>
                  </>
                ) : null}

                {isResultsPicture ? (
                  <div className="ig-filter-group ig-schedule-mode-group">
                    <label>{t('igResultsIncludeUpcoming')}</label>
                    <label className="ig-check-row" htmlFor="ig-include-upcoming">
                      <input
                        id="ig-include-upcoming"
                        type="checkbox"
                        checked={includeUpcoming}
                        onChange={(event) => {
                          setIncludeUpcoming(event.target.checked);
                          resetMatchListPicker();
                          setPreviewHtml('');
                          setFrameHeight(0);
                          setMessage({ text: '', type: '' });
                        }}
                      />
                      <span>{t('igResultsIncludeUpcomingHint')}</span>
                    </label>
                  </div>
                ) : null}


                {canPreview && isMatchListPicture ? (
                  <div className="ig-filter-group ig-groups-picker-group ig-match-list-group">
                    <label className="ig-filter-label-row">
                      <span>{t('igMatchListPick')}</span>
                      {matchListCandidates.length ? (
                        <span className="ig-match-list-count">
                          {t('igMatchListSelected')
                            .replace('{n}', String(matchListSelectedIds.length))
                            .replace('{total}', String(matchListCandidates.length))}
                        </span>
                      ) : null}
                    </label>
                    {matchListCandidates.length ? (
                      <div className="ig-match-list-actions">
                        <button type="button" className="ig-filter-inline-link" onClick={selectAllMatchList}>
                          {t('igMatchListAll')}
                        </button>
                        <button type="button" className="ig-filter-inline-link" onClick={clearMatchListSelection}>
                          {t('igMatchListNone')}
                        </button>
                      </div>
                    ) : null}
                    {matchListLoading ? (
                      <p className="ig-sponsor-hint" style={{ margin: 0 }}>{t('igMatchListLoading')}</p>
                    ) : null}
                    {!matchListLoading && !matchListCandidates.length ? (
                      <p className="ig-sponsor-hint" style={{ margin: 0 }}>{t('igMatchListEmpty')}</p>
                    ) : null}
                    {!matchListLoading && matchListCandidates.length ? (
                      <div className="ig-match-list-picker" role="group" aria-label={t('igMatchListPick')}>
                        {matchListDayGroups.map((group) => (
                          <div key={group.key} className="ig-match-list-day">
                            {group.label ? (
                              <div className="ig-match-list-day-title">{group.label}</div>
                            ) : null}
                            {group.matches.map((match) => {
                              const checked = matchListSelectedIds.includes(match.id);
                              return (
                                <label
                                  key={match.id}
                                  className={`ig-check-row ig-match-list-row${checked ? ' is-checked' : ''}`}
                                  htmlFor={`ig-match-list-${match.id}`}
                                >
                                  <input
                                    id={`ig-match-list-${match.id}`}
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleMatchListId(match.id)}
                                  />
                                  <span>
                                    {formatMatchListCandidateLabel(match, { withCenter: isResultsPicture })}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <p className="ig-sponsor-hint" style={{ margin: 0 }}>{t('igMatchListPickHint')}</p>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>

        <div className="ig-body">
          <aside className="ig-settings" ref={settingsRef}>
            {isMatchCoverGoalPicture ? (
              <div className="ig-settings-block">
                <h3>{t('igCoverGoalSettings')}</h3>
                <p className="ig-sponsor-hint" style={{ margin: 0 }}>{t('igCoverGoalSettingsHint')}</p>
              </div>
            ) : null}
            <div className="ig-settings-block">
              <div className="ig-field-label-row">
                <h3>{t('igTheme')}</h3>
                <div className="ig-under-switch" role="tablist" aria-label={t('igTheme')}>
                  <button
                    type="button"
                    role="tab"
                    className={`ig-under-switch-btn${underGroup === 'pattern' ? ' is-active' : ''}`}
                    aria-selected={underGroup === 'pattern'}
                    onClick={() => setUnderGroup('pattern')}
                  >
                    {t('igUnderPattern')}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    className={`ig-under-switch-btn${underGroup === 'solid' ? ' is-active' : ''}`}
                    aria-selected={underGroup === 'solid'}
                    onClick={() => setUnderGroup('solid')}
                  >
                    {t('igUnderSolid')}
                  </button>
                </div>
              </div>

              <UnderlozhkaGrid
                items={underGroup === 'pattern' ? patternUnderlozhki : solidUnderlozhki}
                activeKey={theme}
                isCustomBg={isCustomBg}
                onSelect={handleSelectPreset}
                uploadSlot={(
                  <button
                    type="button"
                    className={`ig-under-btn ig-under-upload${isCustomBg ? ' is-active' : ''}`}
                    onClick={handleUploadClick}
                    disabled={uploadBusy}
                    title={t('igUploadOwn')}
                  >
                    {isCustomBg ? (
                      <img className="ig-under-thumb" src={customUrl} alt={customFileName} />
                    ) : (
                      <span className="ig-under-upload-icon">
                        {uploadBusy ? <Loader type="TailSpin" color="#3ad1a0" height={22} width={22} /> : <FiUpload />}
                      </span>
                    )}
                    <span className="ig-under-label">
                      {uploadBusy ? t('igUploadBusy') : t('igUploadOwn')}
                    </span>
                  </button>
                )}
              />

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                className="ig-file-input"
                onChange={handleFileChange}
              />
              <input
                ref={previewLeftFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                className="ig-file-input"
                onChange={handleFileChange}
              />
              <input
                ref={previewRightFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                className="ig-file-input"
                onChange={handleFileChange}
              />

              {isCustomBg ? (
                <div className="ig-custom-panel">
                  <div className="ig-custom-meta">
                    <span className="ig-custom-name" title={customFileName}>{customFileName}</span>
                    <button type="button" className="ig-clear-btn" onClick={handleRemoveUpload}>
                      <FiX /> {t('igRemoveUpload')}
                    </button>
                  </div>
                  <div className="ig-field-label">{t('igCustomPalette')}</div>
                  <div className="ig-palette-row">
                    {paletteUnderlozhki.map((item) => (
                      <button
                        key={`palette-${item.key}`}
                        type="button"
                        className={`ig-palette-dot${theme === item.key ? ' is-active' : ''}`}
                        style={{ background: item.accent, boxShadow: `inset 0 0 0 1px ${item.backgroundColor}` }}
                        title={item.label || item.key}
                        onClick={() => handlePaletteOnly(item.key)}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {!isMatchCoverGoalPicture ? (
            <div className="ig-settings-block ig-sponsors-block">
              <div className="ig-styles-header">
                <h3>{t('igSponsors')}</h3>
                {sponsorsLoading ? <Loader type="TailSpin" color="#3ad1a0" height={16} width={16} /> : null}
              </div>
              <p className="ig-sponsor-hint">{t('igSponsorsHint')}</p>

              <div className="ig-sponsors-toolbar">
                <Link to="/infographics/sponsors" className="ig-sponsors-link">
                  <FiExternalLink /> {t('igSponsorsMy')}
                </Link>
              </div>

              {selectedLeague ? (
                <div className="ig-style-group">
                  <div className="ig-under-section-title">{t('igSponsorsLeague')}</div>
                  {Array.isArray(sponsorsCache.leagueSponsors?.sponsors)
                    && sponsorsCache.leagueSponsors.sponsors.length ? (
                    <>
                      <div className="ig-style-chips">
                        <button
                          type="button"
                          className="ig-style-chip"
                          disabled={sponsorsBusy}
                          onClick={() => applySponsorPlacements(
                            sponsorsCache.leagueSponsors.sponsors,
                            sponsorsCache.leagueSponsors.match === 'fallback'
                              ? t('igSponsorsLeagueFallback')
                              : t('igSponsorsLeague')
                          )}
                        >
                          <span className="ig-style-swatch ig-sponsor-swatch" />
                          <span>
                            {t('igSponsorsLeagueApply')}
                            {sponsorsCache.leagueSponsors.match === 'fallback'
                              ? ` (${t('igStyleFallbackShort')})`
                              : ''}
                            {` · ${sponsorsCache.leagueSponsors.sponsors.length}`}
                          </span>
                        </button>
                      </div>
                      <p className="ig-style-hint">{t('igSponsorsLeagueHint')}</p>
                    </>
                  ) : (
                    <p className="ig-style-hint">{t('igSponsorsLeagueEmpty')}</p>
                  )}
                </div>
              ) : null}

              {selectedLeague ? (
                <div className="ig-style-group">
                  <div className="ig-under-section-title">{t('igSponsorsSchemes')}</div>
                  <div className="ig-style-chips">
                    {(sponsorsCache.champSchemes || []).map((scheme) => (
                      <div key={scheme.id} className="ig-style-chip-wrap">
                        <button
                          type="button"
                          className="ig-style-chip"
                          disabled={sponsorsBusy}
                          onClick={() => applySponsorPlacements(
                            scheme.sponsors,
                            scheme.name || t('igSponsorsSchemes')
                          )}
                          title={scheme.name}
                        >
                          <span className="ig-style-swatch ig-sponsor-swatch" />
                          <span>
                            {scheme.name || t('igSponsorsSchemes')}
                            {Array.isArray(scheme.sponsors) ? ` · ${scheme.sponsors.length}` : ''}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="ig-style-chip-delete"
                          disabled={sponsorsBusy}
                          onClick={() => handleDeleteSponsorScheme(scheme.id)}
                          title={t('igSponsorsSchemeDelete')}
                        >
                          <FiX />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="ig-scheme-save">
                    <input
                      className="ig-input"
                      type="text"
                      value={sponsorSchemeName}
                      onChange={(event) => setSponsorSchemeName(event.target.value)}
                      placeholder={t('igSponsorsSchemeNamePh')}
                      maxLength={64}
                    />
                    <button
                      type="button"
                      className="ig-style-action"
                      disabled={sponsorsBusy || !sponsorSchemeName.trim() || !sponsors.length}
                      onClick={handleSaveSponsorScheme}
                    >
                      {t('igSponsorsSchemeSave')}
                    </button>
                  </div>
                </div>
              ) : null}

              {activeSponsorSource ? (
                <div className="ig-style-source">{t('igStyleApplied')}: {activeSponsorSource}</div>
              ) : null}

              {!selectedLeague ? (
                <div className="ig-sponsors-locked" role="status">
                  {t('igSponsorsNeedLeague')}
                </div>
              ) : null}

              <div className={`ig-sponsors-pick-row${!selectedLeague ? ' is-locked' : ''}`}>
                <div className="ig-sponsors-pick-select">
                  <SelectSearch
                    options={sponsorPickOptions}
                    search
                    value={sponsorPickKey || undefined}
                    onChange={(value) => setSponsorPickKey(value || '')}
                    filterOptions={fuzzySearch}
                    emptyMessage={
                      !selectedLeague
                        ? t('igSponsorsPickNeedLeague')
                        : t('igSponsorsPickEmpty')
                    }
                    placeholder={
                      !selectedLeague
                        ? t('igSponsorsPickNeedLeague')
                        : t('igSponsorsPickPh')
                    }
                    disabled={!selectedLeague || !sponsorPickOptions.length || sponsorsBusy || sponsorsLoading}
                    renderOption={(propsOption, option, snapshot, className) => (
                      <button
                        type="button"
                        className={`${className} ig-sponsor-option`}
                        {...propsOption}
                      >
                        {option.src ? (
                          <img src={option.src} alt="" className="ig-sponsor-option-thumb" />
                        ) : (
                          <span className="ig-sponsor-option-thumb is-empty"><FiImage /></span>
                        )}
                        <span className="ig-sponsor-option-label">{option.name}</span>
                      </button>
                    )}
                  />
                </div>
                <button
                  type="button"
                  className="ig-style-action"
                  disabled={
                    !selectedLeague
                    || !sponsorPickKey
                    || !canPreview
                    || !frameHeight
                    || sponsorsBusy
                    || previewLoading
                  }
                  onClick={handleAddPickedSponsor}
                >
                  {t('igSponsorsAddBtn')}
                </button>
              </div>

              <div className="ig-under-section-title">{t('igSponsorsPlaced')}</div>
              <p className="ig-sponsor-hint">{t('igSponsorsAnchorHint')}</p>
              {sponsors.length ? (
                <div className="ig-sponsors-placed">
                  {sponsors.map((item) => {
                    const bottomAnchored = frameHeight
                      ? isSponsorInLowerHalf(item, frameHeight)
                      : item.anchorY === 'bottom';
                    return (
                    <div
                      key={item.id}
                      className={`ig-sponsor-placed-row${selectedSponsorId === item.id ? ' is-active' : ''}${bottomAnchored ? ' is-bottom-anchor' : ''}`}
                    >
                      <button
                        type="button"
                        className="ig-sponsor-placed-main"
                        onClick={() => setSelectedSponsorId(item.id)}
                      >
                        <img src={item.src} alt={item.name} />
                        <span>{item.name}</span>
                      </button>
                      <div className="ig-sponsor-placed-actions">
                        <button
                          type="button"
                          title={t('igSponsorsBringFront')}
                          onClick={() => handleBringSponsorFront(item.id)}
                        >
                          <FiArrowUp />
                        </button>
                        <button
                          type="button"
                          title={t('igSponsorsRemove')}
                          onClick={() => handleRemoveSponsor(item.id)}
                        >
                          <FiX />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p className="ig-sponsor-hint">{t('igSponsorsEmpty')}</p>
              )}
            </div>
            ) : null}

            {isPreviewPicture ? (
              <div className="ig-settings-block">
                <div className="ig-field-label-row">
                  <h3>{t('igPreviewPhotos')}</h3>
                </div>
                <p className="ig-sponsor-hint" style={{ margin: '0 0 10px' }}>{t('igPreviewPhotosHint')}</p>
                <div className="ig-preview-photo-row">
                  <div className="ig-preview-photo-slot">
                    <span className="ig-field-label">{t('igPreviewPhotoLeft')}</span>
                    {previewLeftPhoto ? (
                      <img className="ig-preview-photo-thumb" src={previewLeftPhoto} alt={previewLeftFileName || 'left'} />
                    ) : (
                      <div className="ig-preview-photo-empty">{t('igPreviewPhotoEmpty')}</div>
                    )}
                    <div className="ig-preview-photo-actions">
                      <button type="button" className="ig-clear-btn" onClick={() => handlePreviewPhotoClick('left')} disabled={uploadBusy}>
                        <FiUpload /> {previewLeftPhoto ? t('igPreviewPhotoReplace') : t('igPreviewPhotoUpload')}
                      </button>
                      {previewLeftPhoto ? (
                        <button type="button" className="ig-clear-btn" onClick={() => clearPreviewPhoto('left')}>
                          <FiX /> {t('igRemoveUpload')}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="ig-preview-photo-slot">
                    <span className="ig-field-label">{t('igPreviewPhotoRight')}</span>
                    {previewRightPhoto ? (
                      <img className="ig-preview-photo-thumb" src={previewRightPhoto} alt={previewRightFileName || 'right'} />
                    ) : (
                      <div className="ig-preview-photo-empty">{t('igPreviewPhotoEmpty')}</div>
                    )}
                    <div className="ig-preview-photo-actions">
                      <button type="button" className="ig-clear-btn" onClick={() => handlePreviewPhotoClick('right')} disabled={uploadBusy}>
                        <FiUpload /> {previewRightPhoto ? t('igPreviewPhotoReplace') : t('igPreviewPhotoUpload')}
                      </button>
                      {previewRightPhoto ? (
                        <button type="button" className="ig-clear-btn" onClick={() => clearPreviewPhoto('right')}>
                          <FiX /> {t('igRemoveUpload')}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {!isAfishaPicture && !isQuotePicture ? (
            <div className="ig-settings-block">
              <label className="ig-field-label" htmlFor="ig-title">
                {isMatchCoverGoalPicture
                  ? t('igCoverGoalTitle')
                  : (isBroadcastPreviewPicture
                    ? t('igBroadcastTitle')
                    : (isPreviewPicture
                      ? t('igPreviewHeadline')
                      : (isAnnouncementPicture
                        ? t('igAnnouncementText')
                        : (isAchievementsPicture
                          ? t('igAchievementsTitle')
                          : (isMvpPicture ? t('igMvpLabel') : t('igTitle'))))))}
              </label>
              {isAnnouncementPicture ? (
                <>
                  <textarea
                    id="ig-title"
                    className="ig-input ig-announcement-text"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder={t('igAnnouncementTextPh')}
                    rows={6}
                    maxLength={240}
                  />
                  <p className="ig-sponsor-hint" style={{ margin: '6px 0 0' }}>{t('igAnnouncementTextHint')}</p>
                </>
              ) : (
                <input
                  id="ig-title"
                  className="ig-input"
                  type="text"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder={
                    isMatchCoverGoalPicture || isBroadcastPreviewPicture
                      ? titlePlaceholder
                      : (isPreviewPicture
                        ? t('igPreviewHeadlinePh')
                        : (isAchievementsPicture
                          ? t('igAchievementsTitlePh')
                          : (isMvpPicture ? t('igMvpLabelPh') : titlePlaceholder)))
                  }
                  maxLength={isBroadcastPreviewPicture ? 64 : 48}
                />
              )}
            </div>
            ) : null}

            {isBroadcastPreviewPicture ? (
              <div className="ig-settings-block">
                <label className="ig-field-label" htmlFor="ig-broadcast-tournament">
                  {t('igBroadcastTournamentTitle')}
                </label>
                <input
                  id="ig-broadcast-tournament"
                  className="ig-input"
                  type="text"
                  value={broadcastTournamentTitle}
                  onChange={(event) => setBroadcastTournamentTitle(event.target.value)}
                  placeholder={t('igBroadcastTournamentTitlePh')}
                  maxLength={80}
                />
                <p className="ig-sponsor-hint" style={{ margin: '6px 0 0' }}>
                  {t('igBroadcastTournamentTitleHint')}
                </p>
              </div>
            ) : null}

            {isAchievementsPicture ? (
              <div className="ig-settings-block">
                <label className="ig-field-label" htmlFor="ig-achievement-name">{t('igAchievementsName')}</label>
                <input
                  id="ig-achievement-name"
                  className="ig-input"
                  type="text"
                  value={achievementName}
                  onChange={(event) => setAchievementName(event.target.value)}
                  placeholder={t('igAchievementsNamePh')}
                  maxLength={48}
                />
              </div>
            ) : null}

            {isQuotePicture ? (
              <>
                <div className="ig-settings-block">
                  <label className="ig-field-label" htmlFor="ig-quote-name">{t('igQuoteName')}</label>
                  <input
                    id="ig-quote-name"
                    className="ig-input"
                    type="text"
                    value={quotePersonName}
                    onChange={(event) => setQuotePersonName(event.target.value)}
                    placeholder={t('igQuoteNamePh')}
                    maxLength={40}
                  />
                </div>
                <div className="ig-settings-block">
                  <label className="ig-field-label" htmlFor="ig-quote-text">{t('igQuoteText')}</label>
                  <textarea
                    id="ig-quote-text"
                    className="ig-input ig-announcement-text"
                    value={quoteText}
                    onChange={(event) => setQuoteText(event.target.value)}
                    placeholder={t('igQuoteTextPh')}
                    rows={4}
                    maxLength={200}
                  />
                  <p className="ig-sponsor-hint" style={{ margin: '6px 0 0' }}>{t('igQuoteTextHint')}</p>
                </div>
              </>
            ) : null}

            {!isMvpPicture ? (
            <div className="ig-settings-block">
              <label className="ig-field-label" htmlFor="ig-logo-caption">{t('igLogoCaption')}</label>
              <input
                id="ig-logo-caption"
                className="ig-input"
                type="text"
                value={logoCaption}
                onChange={(event) => setLogoCaption(event.target.value)}
                placeholder={t('igLogoCaptionPh')}
                maxLength={48}
              />
            </div>
            ) : null}

            <div className="ig-settings-block ig-color-row">
              <ColorField
                id="ig-accent"
                label={t('igAccent')}
                value={accent}
                placeholder={selectedUnderlozhka?.accent || '#1550F5'}
                onChange={setAccent}
                onReset={() => setAccent('')}
                resetLabel={t('igReset')}
              />
              {isMatchCoverGoalPicture ? (
                <ColorField
                  id="ig-stadium-bg"
                  label={t('igStadiumBg')}
                  value={stadiumBackground}
                  placeholder={accent || selectedUnderlozhka?.accent || '#1550F5'}
                  onChange={setStadiumBackground}
                  onReset={() => setStadiumBackground('')}
                  resetLabel={t('igReset')}
                />
              ) : (
                <ColorField
                  id="ig-title-color"
                  label={t('igTitleColor')}
                  value={titleColor}
                  placeholder={accent || selectedUnderlozhka?.accent || '#1550F5'}
                  onChange={setTitleColor}
                  onReset={() => setTitleColor('')}
                  resetLabel={t('igReset')}
                />
              )}
            </div>

            {isMatchStatsPicture ? (
              <div className="ig-settings-block">
                <div className="ig-under-section-title">{t('igMatchStatsBars')}</div>
                <p className="ig-sponsor-hint" style={{ margin: '0 0 10px' }}>{t('igMatchStatsBarsHint')}</p>
                <div className="ig-color-row">
                  <ColorField
                    id="ig-match-stats-home-bar"
                    label={
                      matchStatsBarMeta?.homeName
                        ? `${t('igMatchStatsHomeBar')} · ${matchStatsBarMeta.homeName}`
                        : t('igMatchStatsHomeBar')
                    }
                    value={matchStatsHomeBarColor}
                    placeholder={
                      matchStatsBarMeta?.home
                      || matchStatsBarMeta?.homeTeamColor
                      || accent
                      || selectedUnderlozhka?.accent
                      || '#1550F5'
                    }
                    onChange={setMatchStatsHomeBarColor}
                    onReset={() => setMatchStatsHomeBarColor('')}
                    resetLabel={t('igMatchStatsBarReset')}
                    withAlpha
                    alphaLabel={t('igAlpha')}
                  />
                  <ColorField
                    id="ig-match-stats-guest-bar"
                    label={
                      matchStatsBarMeta?.guestName
                        ? `${t('igMatchStatsGuestBar')} · ${matchStatsBarMeta.guestName}`
                        : t('igMatchStatsGuestBar')
                    }
                    value={matchStatsGuestBarColor}
                    placeholder={
                      matchStatsBarMeta?.guest
                      || matchStatsBarMeta?.guestTeamColor
                      || 'rgba(255,255,255,0.45)'
                    }
                    onChange={setMatchStatsGuestBarColor}
                    onReset={() => setMatchStatsGuestBarColor('')}
                    resetLabel={t('igMatchStatsBarReset')}
                    withAlpha
                    alphaLabel={t('igAlpha')}
                  />
                </div>
                {matchStatsBarMeta ? (
                  <p className="ig-sponsor-hint" style={{ margin: '8px 0 0' }}>
                    {[
                      matchStatsBarSourceLabel(matchStatsBarMeta.homeSource)
                        ? `${t('igMatchStatsHomeBar')}: ${matchStatsBarSourceLabel(matchStatsBarMeta.homeSource)}`
                        : '',
                      matchStatsBarSourceLabel(matchStatsBarMeta.guestSource)
                        ? `${t('igMatchStatsGuestBar')}: ${matchStatsBarSourceLabel(matchStatsBarMeta.guestSource)}`
                        : '',
                    ].filter(Boolean).join(' · ')}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="ig-advanced">
              {!isMatchCoverGoalPicture ? (
              <div className="ig-settings-block ig-color-row">
                <ColorField
                  id="ig-table-bg"
                  label={t('igTableBg')}
                  value={tableBackground}
                  placeholder={selectedUnderlozhka?.tableBackground || 'rgba(3, 12, 38, 0.48)'}
                  onChange={setTableBackground}
                  onReset={() => setTableBackground('')}
                  resetLabel={t('igReset')}
                  withAlpha
                  alphaLabel={t('igAlpha')}
                />
                <ColorField
                  id="ig-league-bar-bg"
                  label={t('igLeagueBarBg')}
                  value={leagueBarBackground}
                  placeholder={selectedUnderlozhka?.leagueBarBackground || 'rgba(8, 20, 55, 0.72)'}
                  onChange={setLeagueBarBackground}
                  onReset={() => setLeagueBarBackground('')}
                  resetLabel={t('igReset')}
                  withAlpha
                  alphaLabel={t('igAlpha')}
                />
              </div>
              ) : null}

              <div className="ig-settings-block">
                <h3>{t('igFooter')}</h3>
                <ColorField
                  id="ig-footer-color"
                  label={t('igFooterColor')}
                  value={footerColor}
                  placeholder={DEFAULT_FOOTER_COLOR}
                  onChange={setFooterColor}
                  onReset={() => setFooterColor('')}
                  resetLabel={t('igReset')}
                />
              </div>

              <div className="ig-footer-list">
                <label className={`ig-footer-row${!footerVkEnabled ? ' is-off' : ''}`}>
                  <input
                    type="checkbox"
                    checked={footerVkEnabled}
                    onChange={(event) => setFooterVkEnabled(event.target.checked)}
                  />
                  <span className="ig-footer-name">VK</span>
                  <input
                    className="ig-input"
                    type="text"
                    value={footerVkText}
                    onChange={(event) => setFooterVkText(event.target.value)}
                    placeholder={DEFAULT_FOOTER.vk.text}
                    disabled={!footerVkEnabled}
                  />
                </label>
                <label className={`ig-footer-row${!footerWebEnabled ? ' is-off' : ''}`}>
                  <input
                    type="checkbox"
                    checked={footerWebEnabled}
                    onChange={(event) => setFooterWebEnabled(event.target.checked)}
                  />
                  <span className="ig-footer-name">{t('igFooterWeb')}</span>
                  <input
                    className="ig-input"
                    type="text"
                    value={footerWebText}
                    onChange={(event) => setFooterWebText(event.target.value)}
                    placeholder={DEFAULT_FOOTER.web.text}
                    disabled={!footerWebEnabled}
                  />
                </label>
                <label className={`ig-footer-row${!footerIgEnabled ? ' is-off' : ''}`}>
                  <input
                    type="checkbox"
                    checked={footerIgEnabled}
                    onChange={(event) => setFooterIgEnabled(event.target.checked)}
                  />
                  <span className="ig-footer-name">Instagram</span>
                  <input
                    className="ig-input"
                    type="text"
                    value={footerIgText}
                    onChange={(event) => setFooterIgText(event.target.value)}
                    placeholder={DEFAULT_FOOTER.instagram.text}
                    disabled={!footerIgEnabled}
                  />
                </label>
              </div>
            </div>

            {selectedLeague ? (
              <div className="ig-settings-block ig-styles-block">
                <div className="ig-styles-header">
                  <h3>{t('igStyles')}</h3>
                  {stylesLoading ? <Loader type="TailSpin" color="#3ad1a0" height={16} width={16} /> : null}
                </div>
                {activeStyleSource ? (
                  <div className="ig-style-source">{t('igStyleApplied')}: {activeStyleSource}</div>
                ) : null}

                {stylesCache.leagueStyle?.style ? (
                  <div className="ig-style-group">
                    <div className="ig-under-section-title">{t('igStyleLeague')}</div>
                    <div className="ig-style-chips">
                      <button
                        type="button"
                        className="ig-style-chip"
                        disabled={stylesBusy}
                        onClick={() => applyStoredStyle(
                          stylesCache.leagueStyle.style,
                          stylesCache.leagueStyle.match === 'fallback' ? t('igStyleLeagueFallback') : t('igStyleLeague'),
                          // Fallback = style from another picture type — keep current title
                          { applyTitle: stylesCache.leagueStyle.match !== 'fallback' }
                        )}
                        title={stylePreviewLabel(stylesCache.leagueStyle.style)}
                      >
                        <span
                          className="ig-style-swatch"
                          style={{ background: styleToFormFields(stylesCache.leagueStyle.style).accent || '#3ad1a0' }}
                        />
                        <span>
                          {stylePreviewLabel(stylesCache.leagueStyle.style, t('igStyleLeague'))}
                          {stylesCache.leagueStyle.match === 'fallback' ? ` (${t('igStyleFallbackShort')})` : ''}
                        </span>
                      </button>
                    </div>
                    <p className="ig-style-hint">
                      {isMatchCoverGoalPicture ? t('igStyleLeagueHintZip') : t('igStyleLeagueHint')}
                    </p>
                    <button
                      type="button"
                      className="ig-style-action"
                      disabled={stylesBusy || !selectedLeague}
                      onClick={handleSaveLeagueStyle}
                    >
                      {t('igStyleLeagueSave')}
                    </button>
                  </div>
                ) : selectedLeague ? (
                  <div className="ig-style-group">
                    <div className="ig-under-section-title">{t('igStyleLeague')}</div>
                    <p className="ig-style-hint">
                      {isMatchCoverGoalPicture ? t('igStyleLeagueEmptyZip') : t('igStyleLeagueEmpty')}
                    </p>
                    <button
                      type="button"
                      className="ig-style-action"
                      disabled={stylesBusy}
                      onClick={handleSaveLeagueStyle}
                    >
                      {t('igStyleLeagueSave')}
                    </button>
                  </div>
                ) : null}

                <div className="ig-style-group">
                  <div className="ig-under-section-title">{t('igSchemes')}</div>
                  <div className="ig-style-chips">
                    {(stylesCache.champSchemes || []).map((scheme) => (
                      <div key={scheme.id} className="ig-style-chip-wrap">
                        <button
                          type="button"
                          className="ig-style-chip"
                          disabled={stylesBusy}
                          onClick={() => applyStoredStyle(scheme.style, scheme.name || t('igSchemes'))}
                          title={stylePreviewLabel(scheme.style, scheme.name)}
                        >
                          <span
                            className="ig-style-swatch"
                            style={{ background: styleToFormFields(scheme.style).accent || '#3ad1a0' }}
                          />
                          <span>{scheme.name || stylePreviewLabel(scheme.style)}</span>
                        </button>
                        <button
                          type="button"
                          className="ig-style-chip-delete"
                          disabled={stylesBusy}
                          onClick={() => handleDeleteScheme(scheme.id)}
                          title={t('igSchemeDelete')}
                        >
                          <FiX />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="ig-scheme-save">
                    <input
                      className="ig-input"
                      type="text"
                      value={schemeName}
                      onChange={(event) => setSchemeName(event.target.value)}
                      placeholder={t('igSchemeNamePh')}
                      maxLength={64}
                    />
                    <button
                      type="button"
                      className="ig-style-action"
                      disabled={stylesBusy || !schemeName.trim()}
                      onClick={handleSaveScheme}
                    >
                      {t('igSchemeSave')}
                    </button>
                  </div>
                </div>

                {(stylesCache.userRecent || []).length ? (
                  <div className="ig-style-group">
                    <div className="ig-under-section-title">{t('igRecent')}</div>
                    <div className="ig-style-chips">
                      {stylesCache.userRecent.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className="ig-style-chip"
                          disabled={stylesBusy}
                          onClick={() => applyStoredStyle(item.style, t('igRecent'))}
                          title={stylePreviewLabel(item.style)}
                        >
                          <span
                            className="ig-style-swatch"
                            style={{ background: styleToFormFields(item.style).accent || '#3ad1a0' }}
                          />
                          <span>{stylePreviewLabel(item.style, t('igRecent'))}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </aside>

          <section
            className={[
              'ig-preview',
              previewCollapsed ? 'is-collapsed' : '',
              previewLightbox ? 'is-lightbox' : '',
            ].filter(Boolean).join(' ')}
            ref={previewPanelRef}
          >
            <div className="ig-preview-header">
              <FiImage />
              <span>{t('igPreview')}</span>
              {previewLoading ? (
                <span className="ig-preview-status is-busy">
                  <Loader type="TailSpin" color="#3ad1a0" height={14} width={14} />
                  {t('igLoadingPreview')}
                </span>
              ) : null}
              <div className="ig-preview-actions">
                {!previewCollapsed && !previewLightbox ? (
                  <button
                    type="button"
                    className="ig-preview-action"
                    onClick={() => setPreviewLightbox(true)}
                    title={t('igPreviewOpen')}
                  >
                    <FiMaximize2 />
                    <span>{t('igPreviewOpen')}</span>
                  </button>
                ) : null}
                {previewLightbox ? (
                  <button
                    type="button"
                    className="ig-preview-action"
                    onClick={() => setPreviewLightbox(false)}
                    title={t('igPreviewClose')}
                  >
                    <FiX />
                    <span>{t('igPreviewClose')}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ig-preview-action"
                    onClick={() => {
                      setPreviewLightbox(false);
                      setPreviewCollapsed((prev) => !prev);
                    }}
                    title={previewCollapsed ? t('igPreviewExpand') : t('igPreviewCollapse')}
                  >
                    {previewCollapsed ? <FiChevronUp /> : <FiChevronDown />}
                    <span>{previewCollapsed ? t('igPreviewExpand') : t('igPreviewCollapse')}</span>
                  </button>
                )}
              </div>
            </div>

            {!champId && !champsLoading ? (
              <div className="ig-message error">{t('gmNeedChamp')}</div>
            ) : null}

            {message.text ? (
              <div className={`ig-message ${message.type === 'success' ? 'success' : 'error'}`}>
                {message.text}
              </div>
            ) : null}

            <div
              className="ig-preview-shell"
              ref={previewShellRef}
              role={!previewLightbox ? 'button' : undefined}
              tabIndex={!previewLightbox ? 0 : undefined}
              onClick={() => {
                if (previewLightbox || previewCollapsed) return;
                if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1100px)').matches) {
                  setPreviewLightbox(true);
                }
              }}
              onKeyDown={(event) => {
                if (previewLightbox || previewCollapsed) return;
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1100px)').matches) {
                  setPreviewLightbox(true);
                }
              }}
            >
              {!canPreview ? (
                <div className="ig-preview-empty">{t('igPreviewEmpty')}</div>
              ) : previewHtml ? (
                <div
                  className="ig-preview-stage"
                  style={{
                    width: Math.max(1, frameWidth * scale),
                    height: Math.max(frameHeight * scale, previewLoading ? 220 : 0),
                  }}
                >
                  <div
                    className={`ig-preview-scaler${previewLoading ? ' is-loading' : ''}`}
                    style={{
                      width: Math.max(1, frameWidth * scale),
                      height: Math.max(frameHeight * scale, previewLoading ? 220 : 0),
                      '--ig-scale': scale,
                    }}
                  >
                    <iframe
                      ref={iframeRef}
                      className="ig-preview-frame"
                      title={t('igPreview')}
                      srcDoc={previewHtml}
                      sandbox="allow-same-origin allow-scripts"
                      onLoad={syncIframeHeight}
                      style={{
                        width: frameWidth,
                        height: frameHeight > 0 ? frameHeight : 'auto',
                      }}
                    />
                  </div>
                  {frameHeight > 0 && !isMatchCoverGoalPicture ? (
                    <SponsorOverlay
                      sponsors={sponsors}
                      selectedId={selectedSponsorId}
                      onSelect={setSelectedSponsorId}
                      onChange={handleSponsorChange}
                      canvasWidth={frameWidth || DEFAULT_CANVAS_WIDTH}
                      canvasHeight={frameHeight}
                      disabled={previewLoading}
                    />
                  ) : null}
                </div>
              ) : previewLoading ? (
                <div className="ig-preview-empty" aria-hidden="true" />
              ) : (
                <div className="ig-preview-empty">
                  {message.type === 'error' && message.text ? message.text : t('igPreviewError')}
                </div>
              )}

              {canPreview && previewLoading ? (
                <div className="ig-preview-overlay" aria-live="polite" aria-busy="true">
                  <Loader type="Rings" color="#3ad1a0" height={56} width={56} />
                  <span className="ig-preview-overlay-title">{t('igLoadingPreview')}</span>
                  <span className="ig-preview-overlay-hint">{t('igLoadingPreviewHint')}</span>
                </div>
              ) : null}

              {!previewLightbox && !sponsors.length ? (
                <span className="ig-preview-hitlayer" aria-hidden="true" />
              ) : null}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Infographics;
